import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import pLimit from 'p-limit';
import { RootState, AppDispatch, PayloadMap, getClient } from '@src/core/store';
import { ParameterInfo } from '@src/core/hooks';
import {
  Product,
  Additional,
  AddCartItemRequest,
  AddCartItemTariff,
  Scheduling,
  TariffGroup,
  ScheduleTime,
  SeatMapOverview,
  SeatMapSlotResource,
  SellingMode,
  AdditionalType,
  ProductPackage,
  ProductPackageProduct,
  AddProductPackageToCartRequest,
  TariffTypeVerification,
  TariffVerificationTypeEnum,
  AddCartItemTariffVerificationParams,
  TariffGroupAttribute,
  SelectedTariffGroupAttributes,
} from '@src/core/buyerApiClient';
export type { TariffsPerType, TariffsState } from '@src/domains/tariffs/types/tariff';
import { addToCart } from '@src/features/cart/store/cart';
import {
  getSchedulingFrom,
  getSeatMapBestAllocationStatus,
  SeatMapAllocationAttempt,
  getDistinctScheduleTimes,
  createTariffHash,
  findSchedulingForTime,
  formatSeatMapKey,
  hasSchedulingRequiringSeatSelection,
} from '@src/common/seatMaps';
import {
  type TariffsPerType,
  type TariffsState,
  mountTariffsPerTypeListFromTariffGroup,
  getAdditionalPrice,
  getSelectedTariff,
  getTariffPriceInformation,
  isTariffQtdSelectedAvailable,
  makeSelectedTariffs,
  tariffDepedencyStatus,
  applyTariffsForDateAndAttributes,
  serializePricingTariffSelections,
  tariffsRequireQuantityPricing,
  resolveDefaultTariffFromGroup,
} from '@src/domains/tariffs';
import { acceptableQuantities } from '@src/domains/tariffs/core/tariffSelection';
import { schedulingContainsMinute, schedulingHasAvailableOccupation } from '@src/common/schedulings';
import { overlayRepricedPrices } from '@src/domains/scheduling/repricing';
import { computeVisibleRange } from '@src/domains/scheduling/core/computeVisibleRange';
import { initialMonth } from '@src/common/calendar';
import {
  decidePackageBookingSteps,
  isPackageCommonHourSelectionValid,
  packageRequiresTimeStep,
  replicateSingleBookingSelection,
  resolvePackageQuantityRange,
  mapPackageProductForSingleBookingQuantityLimits,
  resolvePackageSingleBookingQuantityLimits,
  shouldUseSingleBookingPackageBooking,
} from '@src/domains/productPackage';
import { pickFirstScheduledProductId } from '@src/domains/products/core/pickFirstScheduledProductId';
import { resolveSchedulingsForDate } from '@src/domains/scheduling/core/resolveSchedulingsForDate';
import { getPaginationTotalCount } from '@src/common';
import { track } from '@src/services/analytics';
import { getAdditionalAvailability, validateAdditionalQuantity } from '@src/common/additionals';
import { additionalsSelectionIsComplete } from '@src/domains/products/core/additionalsSelectionIsComplete';
import type { LanguageOption } from '@src/domains/products/features/product-booking/types/language';
import {
  LANGUAGE_ATTRIBUTE_TYPE,
  isLanguageAttributeType,
  type AttributeOptionGroup,
  type SelectedProductAttribute,
  type TariffGroupAttributeType,
} from '@src/domains/products/features/product-booking/types/attribute';
import {
  orderedAttributeTypes,
  isActiveAttribute,
} from '@src/domains/products/features/product-booking/core/attributes/buildAttributeOptions';
import { upsertSelectedAttributes } from '@src/domains/products/features/product-booking/core/attributes/selectedAttributes';
import {
  buildAttributeSlotsForSchedulings,
  pruneSelectedAttributes,
  schedulingsForTariffMountWithAttributes,
} from '@src/domains/products/scheduleHours';
import { advanceMultiProductBooking, multiProductBookingSelector } from '@src/stores/multiProductBooking';
import { trackUpsellBookingCompleted } from '@src/domains/products/features/productUpsell/analytics/upsellTracking';

export type HourSeatMapValidationStatus = 'idle' | 'loading' | 'completed';

export interface HourSeatMapValidationState {
  status: HourSeatMapValidationStatus;
  results: { [key: string]: SeatMapAllocationAttempt }; // key = schedulingId-minutes
  tariffHash: string; // to invalidate cache when tariffs change
}

const initialHourSeatMapValidationState: HourSeatMapValidationState = {
  status: 'idle',
  results: {},
  tariffHash: '',
};

export type RepricingStatus = 'idle' | 'loading' | 'error';

export interface ProductState {
  products: { [key: string]: FullProduct };
  packages: { [key: string]: PackageState };
  loadingProducts: { [key: string]: boolean };
  loadingSchedulingSeatMap: { [key: string]: boolean };
  productSchedules: { [key: string]: ProductScheduleState };
  seatMaps: { [key: string]: SeatMapOverview | undefined };
  adderState: { [key: string]: ProductAdderState };
  highlightedProductAdder?: HighlightedProductAdder;
  tariffVerifications: { [key: string]: TariffTypeVerification[] };
  loadingTariffVerifications: { [key: string]: boolean };
  hourSeatMapValidation: { [widgetId: string]: HourSeatMapValidationState };
  tariffGroupAttributes: { [productId: string]: TariffGroupAttribute[] };
  loadingTariffGroupAttributes: { [productId: string]: boolean };
  repricedSchedulings: { [productId: string]: { [selectionsHash: string]: Scheduling[] } };
  repricingByWidget: { [widgetId: string]: { statusByTariffId: Record<string, RepricingStatus>; appliedHash: string } };
}

export type ProductPackageStatus = 'idle' | 'loading' | 'loaded';
export interface PackageState {
  productPackage?: ProductPackage;
  productPackageProducts?: ProductPackageProduct[];
  error?: string;
  status?: ProductPackageStatus;
  currentProductIndexAtAdder?: number;
}

export type FullProduct = Product & {
  additionals: Array<Additional>;
};

export interface ProductScheduleState {
  loadedKeys: { [key: string]: boolean };
  dates: {
    [key: string]: {
      availables: Scheduling[];
      unavailables: Scheduling[];
    };
  };
  defaultTariffGroup?: TariffGroup;
  pending: boolean;
}

type CurrentProductAdderStatus = 'ongoing' | 'adding_to_cart' | 'finished_request';

export type TariffWithVerificationValues = {
  tariffTypeId: string;
  verificationValues?: Array<
    AddCartItemTariffVerificationParams & {
      tariffUnitIndex: number;
      verificationPosition: number;
    }
  >;
};

export interface ProductAdderState {
  productId: string;
  packageId?: string;
  currentStatus: CurrentProductAdderStatus;
  addingToCart: boolean;
  autoSelectProducts?: boolean;
  isClosingAdderModal?: boolean;
  productSelectionState: Record<string, ProductSelectionState>;
  tariffVerificationErrors: Record<string, string>;
}

export interface ProductSelectionState {
  currentProductAdderStep?: ProductStepType;
  tariffsState: TariffsInformation;
  selectedTariffHour?: ScheduleTime;
  productAdditionals: { [key: string]: ProductAdditionalState };
  selectedResources: SelectedResource[];
  resourcesAreOk: boolean;
  exceedingValueCents: number;
  seatMapAllocationStatus: SeatMapAllocationAttempt;
  tariffVerificationValues: TariffWithVerificationValues[];
  selectedAttributes: SelectedProductAttribute[];
}

interface AddToCartPayloadProps {
  productId: string;
  selectedTariffs: TariffsPerType[];
  selectedDate?: string;
  selectedTariffHour?: ScheduleTime;
  selectedAdditionals: ProductAdditionalState[];
  selectedResources: SelectedResource[];
  tariffVerificationValues: TariffWithVerificationValues[];
  selectedAttributes?: SelectedProductAttribute[];
}

export interface SelectedResource {
  resourceSlot: SeatMapSlotResource;
  tariffs: SelectedResouceTariff[];
}

export interface SelectedResouceTariff {
  tariffId: string;
  quantity: number;
}

export interface TariffsInformation {
  selectedDate?: string;
  listOfTariffsPerType: TariffsPerType[];
}

export interface ProductAdditionalState {
  checked: boolean;
  input: string;
  additional: Additional;
  quantity?: number;
}

export interface HighlightedProductAdder {
  id: string;
  params: ParameterInfo;
}

const productSteps = ['calendar', 'tariff', 'package_quantity', 'seat_map', 'additional', 'finish'] as const;
export type ProductStepType = (typeof productSteps)[number];

/** Product adder step plus embed-only `time` (hour) validation split from `tariff`. */
export type ProductAdderValidationStep = ProductStepType | 'time';

export type IsProductAdderStepValidOptions = {
  /** When true, tariff row does not require selected hour (embed defers hour to `time` step). */
  skipTariffHourCheck?: boolean;
  /**
   * When true, tariff validity ignores `seatMapAllocationStatus.success` so the user can re-open
   * `time` to fix a failed allocation. The add-to-cart guard never opts in, so checkout stays blocked.
   */
  skipSeatMapAllocationCheck?: boolean;
};

export const createInitialProductSelectionState = () => ({
  productAdditionals: {},
  selectedResources: [],
  resourcesAreOk: false,
  exceedingValueCents: 0,
  tariffsState: { listOfTariffsPerType: [] },
  seatMapAllocationStatus: { success: true, missingCapacity: 0, missingTariffs: 0 },
  tariffVerificationValues: [],
  tariffVerificationErrors: {},
  selectedAttributes: [],
  currentProductAdderStep: undefined as ProductStepType | undefined,
  selectedTariffHour: undefined as ScheduleTime | undefined,
});

export const initialProductSelectionState = createInitialProductSelectionState();

const resolveProductSelection = (adder: ProductAdderState, id: string | undefined) =>
  (id ? adder.productSelectionState[id] : undefined) ?? initialProductSelectionState;

const initialProductAdderState = (productId: string, packageId?: string): ProductAdderState => ({
  productId,
  packageId,
  addingToCart: false,
  autoSelectProducts: false,
  isClosingAdderModal: false,
  currentStatus: 'ongoing',
  productSelectionState: {
    [productId]: createInitialProductSelectionState(),
  },
  tariffVerificationErrors: {},
});

const initialState: ProductState = {
  products: {},
  loadingProducts: {},
  loadingSchedulingSeatMap: {},
  productSchedules: {},
  adderState: {},
  seatMaps: {},
  packages: {},
  tariffVerifications: {},
  loadingTariffVerifications: {},
  hourSeatMapValidation: {},
  tariffGroupAttributes: {},
  loadingTariffGroupAttributes: {},
  repricedSchedulings: {},
  repricingByWidget: {},
};

const productLoadTimeouts: Record<string, ReturnType<typeof setTimeout>> = {};

/**
 * Without-date products have no calendar, so the shopper never picks a date to drive the
 * tariff list. Instead of synthesizing a `selectAvailableDate(null)` event from a component
 * effect, we mount the default tariff group's tariffs directly into every adder pointed at
 * this product. Called from both `receiveTariffGroup` (group arrives) and `setProduct`
 * (product arrives) so the mount happens regardless of which resolves first — the v2 embed
 * dispatches the group load independently from the (setTimeout-delayed) product load.
 * Idempotent: a non-empty list is left untouched so quantities the shopper already set
 * survive a re-render.
 */
function mountWithoutDateTariffs(state: ProductState, productId: string) {
  const product = state.products[productId];
  if (product?.sellingMode !== SellingMode.WithoutDate) return;

  const tariffGroup = state.productSchedules[productId]?.defaultTariffGroup;
  if (!tariffGroup) return;

  const minSaleTariffQuantities = product.minSaleTariffQuantity ?? 0;

  for (const widgetId in state.adderState) {
    const adder = state.adderState[widgetId];
    if (adder?.productId !== productId) continue;

    const selection = adder.productSelectionState[productId];
    if (!selection || selection.tariffsState.listOfTariffsPerType.length > 0) continue;

    selection.tariffsState.listOfTariffsPerType = mountTariffsPerTypeListFromTariffGroup(
      tariffGroup,
      minSaleTariffQuantities,
    );
  }
}

// Reducers only see this slice's state, so unlike isSingleBookingPackageBookingFromState (which
// reads state.auth directly) the feature flag can't be checked in here — the caller resolves the
// full isSingleBooking decision (normally via useIsSingleBookingPackageBooking, which already
// accounts for the flag) and threads it through the action payload. Trusting configurationMode
// alone would keep using the package-wide minimum even after a flag rollback, while the rest of
// the adder already fell back to the per-product flow.
function resolveMinSaleTariffQuantityForAdder(
  state: ProductState,
  widgetId: string,
  product: FullProduct | undefined,
  isSingleBookingPackageBooking: boolean,
): number {
  const packageId = state.adderState[widgetId]?.packageId;
  if (packageId && isSingleBookingPackageBooking) {
    const packageProducts = state.packages[packageId]?.productPackageProducts ?? [];
    return resolvePackageQuantityRange(
      packageProducts.map((item) => ({
        minSaleTariffQuantity: item.product?.minSaleTariffQuantity,
        maxSaleTariffQuantity: item.product?.maxSaleTariffQuantity,
      })),
    ).min;
  }

  return product?.minSaleTariffQuantity ?? 0;
}

// Only selectAvailableDate is dispatched from a package-aware screen (ProductAdderCalendar); the
// field is optional so every other PayloadMap<string> caller (including existing tests) is
// unaffected and simply falls back to the per-product minimum.
type SelectAvailableDatePayload = PayloadMap<string> & { isSingleBookingPackageBooking?: boolean };

export const productSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    setProduct: (state: ProductState, { payload: prod }: PayloadAction<FullProduct>) => {
      state.products[prod.id] = prod;

      // Cover the load-order race: if the default tariff group arrived before the product was
      // in state, receiveTariffGroup couldn't mount yet (selling mode unknown). Retry here now
      // that the product is known. No-op for date-based products and when already mounted.
      mountWithoutDateTariffs(state, prod.id);
    },
    setProductLoad: {
      reducer(state, { payload, meta: { productId } }: PayloadAction<boolean, string, { productId: string }>) {
        state.loadingProducts[productId] = payload;
      },
      prepare(productId: string, loading: boolean) {
        return { payload: loading, meta: { productId } };
      },
    },
    setSeatMapLoading: {
      reducer(state, { payload, meta: { key } }: PayloadAction<boolean, string, { key: string }>) {
        state.loadingSchedulingSeatMap[key] = payload;
      },
      prepare(schedulingId: string, timeMinutes: number | undefined, selectionsHash: string, loading: boolean) {
        return { payload: loading, meta: { key: formatSeatMapKey(schedulingId, timeMinutes, selectionsHash) } };
      },
    },
    initializeAdderState: {
      reducer(
        state,
        {
          payload,
          meta: { productId, packageId },
        }: PayloadAction<string, string, { productId: string; packageId: string }>,
      ) {
        state.adderState[payload] = initialProductAdderState(productId, packageId);
        mountWithoutDateTariffs(state, productId);
      },
      prepare(key: string, productId: string, packageId: string) {
        return { payload: key, meta: { productId, packageId } };
      },
    },
    updateAdderStateProductId: (state: ProductState, { payload }: PayloadAction<PayloadMap<string>>) => {
      const adderState = state.adderState[payload.id];

      if (adderState) {
        adderState.productId = payload.value;
      }
    },
    updateAdderStateProductSelectionState: (
      state: ProductState,
      { payload }: PayloadAction<PayloadMap<Record<string, ProductSelectionState>>>,
    ) => {
      const adderState = state.adderState[payload.id];

      if (adderState) {
        adderState.productSelectionState = payload.value;
      }
    },
    mountDefaultTariffs: (state: ProductState, { payload: productId }: PayloadAction<string>) => {
      mountWithoutDateTariffs(state, productId);
    },
    requestAvailableDates: (state: ProductState, { payload: id }: PayloadAction<string, string>) => {
      if (state.productSchedules[id]) {
        state.productSchedules[id]!.pending = true;
      } else {
        state.productSchedules[id] = {
          loadedKeys: {},
          dates: {},
          pending: true,
        };
      }
    },
    receiveAvailableDates: {
      reducer(
        state,
        {
          payload: schedulings,
          meta: { productId, key },
        }: PayloadAction<Scheduling[], string, { productId: string; key: string }>,
      ) {
        const productSchedules = state.productSchedules[productId];
        if (productSchedules === undefined) {
          return;
        }

        productSchedules.loadedKeys[key] = true;
        productSchedules.pending = false;

        const dates = schedulings.reduce((carry, sch) => {
          const isAvailable = schedulingHasAvailableOccupation(sch);

          const availables = carry?.[sch.date]?.availables ?? [];
          const unavailables = carry?.[sch.date]?.unavailables ?? [];

          return {
            ...carry,
            [sch.date]: {
              availables: isAvailable ? [...availables, sch] : availables,
              unavailables: isAvailable ? unavailables : [...unavailables, sch],
            },
          };
        }, {} as ProductScheduleState['dates']);

        for (const key in dates) {
          productSchedules.dates![key] = dates[key]!;
        }
      },
      prepare(productId: string, key: string, payload: Scheduling[]) {
        return { payload, meta: { productId, key } };
      },
    },
    requestTariffGroup: (state: ProductState, { payload: id }: PayloadAction<string, string>) => {
      if (state.productSchedules[id]) {
        state.productSchedules[id]!.pending = true;
      } else {
        state.productSchedules[id] = {
          loadedKeys: {},
          dates: {},
          pending: true,
        };
      }
    },
    receiveTariffGroup: {
      reducer(
        state,
        {
          payload: tariffGroup,
          meta: { productId, key },
        }: PayloadAction<TariffGroup, string, { productId: string; key: string }>,
      ) {
        const productSchedules = state.productSchedules[productId];
        if (productSchedules === undefined) {
          return;
        }

        productSchedules.loadedKeys[key] = true;
        productSchedules.pending = false;
        productSchedules.defaultTariffGroup = tariffGroup;

        mountWithoutDateTariffs(state, productId);
      },
      prepare(productId: string, key: string, payload: TariffGroup) {
        return { payload, meta: { productId, key } };
      },
    },
    finishRequest: (state: ProductState, { payload: id }: PayloadAction<string, string>) => {
      state.productSchedules[id]!.pending = false;
    },
    updateProductAvailableDateTariff: (state: ProductState, action: PayloadAction<PayloadMap<TariffsPerType>>) => {
      const id = action.payload.id;
      const tariffPerType = action.payload.value;
      const typeId = tariffPerType.type.id;
      const newTariffPerType = action.payload.value;
      const adderState = state.adderState[id];

      if (adderState) {
        const productId = adderState.productId;
        const tariffsState = adderState.productSelectionState[productId]!.tariffsState;

        tariffsState.listOfTariffsPerType = tariffsState.listOfTariffsPerType.map((tariff) => {
          return tariff.type.id === typeId
            ? { ...tariff, selectedQuantity: newTariffPerType.selectedQuantity }
            : tariff;
        });
      }
    },
    selectAvailableDate: (state: ProductState, action: PayloadAction<SelectAvailableDatePayload>) => {
      const adderState = state.adderState[action.payload.id];
      if (adderState == null) {
        return;
      }

      const { productId } = adderState!;
      const tariffsState = adderState.productSelectionState[productId]!.tariffsState;

      const productSchedules = state.productSchedules[productId];
      const product = state.products[productId];
      const minSaleTariffQuantities = resolveMinSaleTariffQuantityForAdder(
        state,
        action.payload.id,
        product,
        action.payload.isSingleBookingPackageBooking ?? false,
      );

      const { value: selectedDate } = action.payload;
      if (tariffsState && productSchedules) {
        tariffsState.selectedDate = selectedDate;

        const schedulings = resolveSchedulingsForDate(productSchedules.dates, selectedDate);
        if (schedulings) {
          const attributes = state.tariffGroupAttributes[productId] ?? [];
          const selectionState = adderState.productSelectionState[productId]!;

          // A value the previous date offered may not exist on the new one — drop it (and
          // anything it cascaded into) so the shopper is asked again instead of silently
          // carrying a selection the date cannot honour.
          const selectedAttributes = pruneSelectedAttributes(
            attributes,
            schedulings.availables,
            selectionState.selectedAttributes,
          );
          selectionState.selectedAttributes = selectedAttributes;

          const slots = buildAttributeSlotsForSchedulings(attributes, schedulings.availables, selectedAttributes);

          applyTariffsForDateAndAttributes(
            product!.sellingMode,
            tariffsState,
            schedulingsForTariffMountWithAttributes(
              schedulings.availables,
              schedulings.unavailables,
              slots,
              selectedAttributes,
            ),
            attributes,
            selectedAttributes,
            minSaleTariffQuantities,
          );
        }
      }

      adderState.productSelectionState[productId]!.selectedTariffHour = undefined;

      // Clear repriced hash, status, and seat map cache when date changes
      const widgetId = action.payload.id;
      if (state.repricingByWidget[widgetId]) {
        state.repricingByWidget[widgetId] = { statusByTariffId: {}, appliedHash: '' };
      }
      state.hourSeatMapValidation[widgetId] = { ...initialHourSeatMapValidationState };
    },
    changeProductAddStep: (state: ProductState, { payload }: PayloadAction<PayloadMap<ProductStepType>>) => {
      const adderState = state.adderState[payload.id]!;
      const productId = adderState.productId;

      adderState.productSelectionState[productId]!.currentProductAdderStep = payload.value;
    },
    changeSelectedHour: (state: ProductState, action: PayloadAction<PayloadMap<ScheduleTime | undefined>>) => {
      const adderState = state.adderState[action.payload.id]!;
      const productId = adderState.productId;

      adderState.productSelectionState[productId]!.selectedTariffHour = action.payload.value;
    },
    /**
     * Upsert by `type`: the incoming values replace the ones already chosen for the same types
     * and leave the others alone. The pruning step then removes whatever the new selection
     * cascaded away — picking a language served by a single tariff group, for instance, drops a
     * pickup zone that only exists in another one.
     */
    changeSelectedAttributes: (state: ProductState, action: PayloadAction<PayloadMap<SelectedProductAttribute[]>>) => {
      const adderState = state.adderState[action.payload.id]!;
      const productId = adderState.productId;
      const selectionState = adderState.productSelectionState[productId]!;

      const merged = upsertSelectedAttributes(selectionState.selectedAttributes, action.payload.value);
      selectionState.selectedAttributes = merged;
      selectionState.selectedTariffHour = undefined;

      const selectedDate = selectionState.tariffsState.selectedDate;
      const schedulings = selectedDate
        ? resolveSchedulingsForDate(state.productSchedules[productId]?.dates, selectedDate)
        : undefined;
      if (selectedDate && schedulings) {
        const attributes = state.tariffGroupAttributes[productId] ?? [];
        const product = state.products[productId];
        // Attribute selection only exists in the product-booking (non-package) flow, so this
        // widget never has a packageId and the single-booking branch never applies here.
        const minSaleTariffQuantities = resolveMinSaleTariffQuantityForAdder(state, action.payload.id, product, false);

        const selectedAttributes = pruneSelectedAttributes(attributes, schedulings.availables, merged);
        selectionState.selectedAttributes = selectedAttributes;

        const slots = buildAttributeSlotsForSchedulings(attributes, schedulings.availables, selectedAttributes);

        applyTariffsForDateAndAttributes(
          product!.sellingMode,
          selectionState.tariffsState,
          schedulingsForTariffMountWithAttributes(
            schedulings.availables,
            schedulings.unavailables,
            slots,
            selectedAttributes,
          ),
          attributes,
          selectedAttributes,
          minSaleTariffQuantities,
        );

        const widgetId = action.payload.id;
        if (state.repricingByWidget[widgetId]) {
          state.repricingByWidget[widgetId] = { statusByTariffId: {}, appliedHash: '' };
        }
      }
    },
    setTariffGroupAttributesLoading: {
      reducer(state, { payload, meta: { productId } }: PayloadAction<boolean, string, { productId: string }>) {
        state.loadingTariffGroupAttributes[productId] = payload;
      },
      prepare(productId: string, loading: boolean) {
        return { payload: loading, meta: { productId } };
      },
    },
    setTariffGroupAttributes: {
      reducer(
        state,
        { payload, meta: { productId } }: PayloadAction<TariffGroupAttribute[], string, { productId: string }>,
      ) {
        state.tariffGroupAttributes[productId] = payload;
      },
      prepare(productId: string, attributes: TariffGroupAttribute[]) {
        return { payload: attributes, meta: { productId } };
      },
    },
    updateProductAdditional: (state: ProductState, { payload }: PayloadAction<PayloadMap<ProductAdditionalState>>) => {
      const adderState = state.adderState[payload.id]!;
      const productId = adderState.productId;

      adderState.productSelectionState[productId]!.productAdditionals[payload.value.additional.id] = payload.value;
    },
    setSelectedResources: {
      reducer(
        state,
        {
          payload,
          meta: { key, isOk, exceedingValueCents },
        }: PayloadAction<SelectedResource[], string, { key: string; isOk: boolean; exceedingValueCents: number }>,
      ) {
        const adderState = state.adderState[key]!;
        const productId = adderState.productId;

        adderState.productSelectionState[productId]!.selectedResources = payload;
        adderState.productSelectionState[productId]!.resourcesAreOk = isOk;
        adderState.productSelectionState[productId]!.exceedingValueCents = exceedingValueCents;
      },
      prepare(key: string, payload: SelectedResource[], isOk: boolean, exceedingValueCents: number) {
        return { payload, meta: { key, isOk, exceedingValueCents } };
      },
    },
    setSeatMapAllocationStatus: {
      reducer(state, { payload, meta: { key } }: PayloadAction<SeatMapAllocationAttempt, string, { key: string }>) {
        const adderState = state.adderState[key]!;
        const productId = adderState.productId;

        adderState!.productSelectionState[productId]!.seatMapAllocationStatus = payload;
      },
      prepare(key: string, payload: SeatMapAllocationAttempt) {
        return { payload, meta: { key } };
      },
    },
    setProductAdderStatus: (state: ProductState, { payload }: PayloadAction<PayloadMap<CurrentProductAdderStatus>>) => {
      const adderState = state.adderState[payload.id]!;
      const productId = adderState.productId;

      adderState.currentStatus = payload.value;

      if (payload.value === 'finished_request') {
        adderState.productSelectionState[productId]!.currentProductAdderStep = 'finish';
      }
    },
    setSeatMap: {
      reducer(
        state,
        {
          payload: seatMap,
          meta: { schedulingId, timeMinutes, selectionsHash },
        }: PayloadAction<
          SeatMapOverview | undefined,
          string,
          { schedulingId: string; timeMinutes?: number; selectionsHash?: string }
        >,
      ) {
        const key = formatSeatMapKey(schedulingId, timeMinutes, selectionsHash);
        state.seatMaps[key] = seatMap;
      },
      prepare(schedulingId: string, timeMinutes?: number, payload?: SeatMapOverview, selectionsHash?: string) {
        return { payload, meta: { schedulingId, timeMinutes, selectionsHash } };
      },
    },
    setRepricingStatus: {
      reducer(
        state,
        {
          payload: { status, tariffIds },
          meta: { widgetId },
        }: PayloadAction<{ status: RepricingStatus; tariffIds: string[] }, string, { widgetId: string }>,
      ) {
        const current = state.repricingByWidget[widgetId] ?? { statusByTariffId: {}, appliedHash: '' };
        state.repricingByWidget[widgetId] = current;
        if (tariffIds.length > 0) {
          for (const id of tariffIds) {
            current.statusByTariffId[id] = status;
          }
        } else {
          for (const id of Object.keys(current.statusByTariffId)) {
            current.statusByTariffId[id] = status;
          }
        }
      },
      prepare(widgetId: string, status: RepricingStatus, tariffIds: string[] = []) {
        return { payload: { status, tariffIds }, meta: { widgetId } };
      },
    },
    receiveRepricedSchedulings: {
      reducer(
        state,
        {
          payload: schedulings,
          meta: { productId, hash },
        }: PayloadAction<Scheduling[], string, { productId: string; hash: string }>,
      ) {
        const bucket = (state.repricedSchedulings[productId] ??= {});
        bucket[hash] = schedulings;
        const keys = Object.keys(bucket);
        if (keys.length > MAX_REPRICED_CACHE_SIZE) {
          const activeHashes = new Set(
            Object.entries(state.repricingByWidget)
              .filter(
                ([widgetId, repricing]) => repricing.appliedHash && state.adderState[widgetId]?.productId === productId,
              )
              .map(([, repricing]) => repricing.appliedHash),
          );
          const evictedHash = keys.find((key) => !activeHashes.has(key));
          if (evictedHash) delete bucket[evictedHash];
        }
      },
      prepare(productId: string, hash: string, payload: Scheduling[]) {
        return { payload, meta: { productId, hash } };
      },
    },
    applyRepricedToTariffs: {
      reducer(state, { payload: hash, meta: { widgetId } }: PayloadAction<string, string, { widgetId: string }>) {
        const current = state.repricingByWidget[widgetId] ?? { statusByTariffId: {}, appliedHash: '' };
        state.repricingByWidget[widgetId] = { ...current, appliedHash: hash };
      },
      prepare(widgetId: string, hash: string) {
        return { payload: hash, meta: { widgetId } };
      },
    },
    setHighlightedProductAdder: {
      reducer(
        state,
        { payload: params, meta: { widgetId: id } }: PayloadAction<ParameterInfo, string, { widgetId: string }>,
      ) {
        state.highlightedProductAdder = { id, params };
      },
      prepare(widgetId: string, params: ParameterInfo) {
        return { payload: params, meta: { widgetId } };
      },
    },
    clearHighlightedProductAdder: (state: ProductState) => {
      state.highlightedProductAdder = undefined;
    },
    setPackage: {
      reducer(state, { payload: productPackageState, meta: packageId }: PayloadAction<PackageState, string, string>) {
        state.packages[packageId] = productPackageState;
      },
      prepare(packageId: string, packageState: PackageState) {
        return { payload: packageState, meta: packageId };
      },
    },
    updateIsClosingAdderModal: {
      reducer(state, { payload: id, meta: value }: PayloadAction<string, string, boolean>) {
        state.adderState[id]!.isClosingAdderModal = value;
      },
      prepare(id: string, value: boolean) {
        return { payload: id, meta: value };
      },
    },
    updateAdderStateAutoSelectProducts: (state: ProductState, { payload }: PayloadAction<PayloadMap<boolean>>) => {
      const adderState = state.adderState[payload.id];

      if (adderState) {
        adderState.autoSelectProducts = payload.value;
      }
    },
    setTariffVerificationsLoading: {
      reducer(state, { payload, meta: { tariffTypeId } }: PayloadAction<boolean, string, { tariffTypeId: string }>) {
        state.loadingTariffVerifications[tariffTypeId] = payload;
      },
      prepare(tariffTypeId: string, loading: boolean) {
        return { payload: loading, meta: { tariffTypeId } };
      },
    },
    setTariffVerifications: {
      reducer(
        state,
        {
          payload: verifications,
          meta: { tariffTypeId },
        }: PayloadAction<TariffTypeVerification[], string, { tariffTypeId: string }>,
      ) {
        state.tariffVerifications[tariffTypeId] = verifications;
      },
      prepare(tariffTypeId: string, verifications: TariffTypeVerification[]) {
        return { payload: verifications, meta: { tariffTypeId } };
      },
    },
    updateTariffVerificationValues: {
      reducer(
        state,
        {
          payload: { tariffTypeId, values },
          meta: { widgetId, productId },
        }: PayloadAction<
          { tariffTypeId: string; values: TariffWithVerificationValues['verificationValues'] },
          string,
          { widgetId: string; productId: string }
        >,
      ) {
        const adderState = state.adderState[widgetId];
        if (adderState && productId && adderState.productSelectionState[productId]) {
          const tariffIndex = adderState.productSelectionState[productId]!.tariffVerificationValues.findIndex(
            (verification) => verification.tariffTypeId === tariffTypeId,
          );

          if (tariffIndex !== -1) {
            const tariff = adderState.productSelectionState[productId]!.tariffVerificationValues[tariffIndex];
            tariff!.verificationValues = values;
          } else {
            adderState.productSelectionState[productId]!.tariffVerificationValues.push({
              tariffTypeId,
              verificationValues: values,
            });
          }
        }
      },
      prepare(
        widgetId: string,
        productId: string,
        { tariffTypeId, values }: { tariffTypeId: string; values: TariffWithVerificationValues['verificationValues'] },
      ) {
        return {
          payload: { tariffTypeId, values },
          meta: { widgetId, productId },
        };
      },
    },
    setHourSeatMapValidationStatus: {
      reducer(
        state,
        {
          payload: status,
          meta: { widgetId },
        }: PayloadAction<HourSeatMapValidationStatus, string, { widgetId: string }>,
      ) {
        if (!state.hourSeatMapValidation[widgetId]) {
          state.hourSeatMapValidation[widgetId] = { ...initialHourSeatMapValidationState };
        }
        state.hourSeatMapValidation[widgetId]!.status = status;
      },
      prepare(widgetId: string, status: HourSeatMapValidationStatus) {
        return { payload: status, meta: { widgetId } };
      },
    },
    setHourSeatMapValidationResult: {
      reducer(
        state,
        {
          payload: result,
          meta: { widgetId, key },
        }: PayloadAction<SeatMapAllocationAttempt, string, { widgetId: string; key: string }>,
      ) {
        if (!state.hourSeatMapValidation[widgetId]) {
          state.hourSeatMapValidation[widgetId] = { ...initialHourSeatMapValidationState };
        }
        state.hourSeatMapValidation[widgetId]!.results[key] = result;
      },
      prepare(widgetId: string, key: string, result: SeatMapAllocationAttempt) {
        return { payload: result, meta: { widgetId, key } };
      },
    },
    setHourSeatMapTariffHash: {
      reducer(state, { payload: tariffHash, meta: { widgetId } }: PayloadAction<string, string, { widgetId: string }>) {
        if (!state.hourSeatMapValidation[widgetId]) {
          state.hourSeatMapValidation[widgetId] = { ...initialHourSeatMapValidationState };
        }
        state.hourSeatMapValidation[widgetId]!.tariffHash = tariffHash;
      },
      prepare(widgetId: string, tariffHash: string) {
        return { payload: tariffHash, meta: { widgetId } };
      },
    },
    clearHourSeatMapValidation: (state: ProductState, { payload: widgetId }: PayloadAction<string>) => {
      state.hourSeatMapValidation[widgetId] = { ...initialHourSeatMapValidationState };
    },
  },
});

export default productSlice.reducer;

export const {
  setProduct,
  setProductLoad,
  requestTariffGroup,
  receiveTariffGroup,
  requestAvailableDates,
  receiveAvailableDates,
  finishRequest,
  setSeatMap,
  initializeAdderState,
  changeProductAddStep,
  selectAvailableDate,
  updateProductAvailableDateTariff,
  setProductAdderStatus,
  setHighlightedProductAdder,
  clearHighlightedProductAdder,
  changeSelectedHour,
  changeSelectedAttributes,
  setTariffGroupAttributesLoading,
  setTariffGroupAttributes,
  setSelectedResources,
  setSeatMapAllocationStatus,
  setSeatMapLoading,
  setPackage,
  updateIsClosingAdderModal,
  updateAdderStateAutoSelectProducts,
  updateAdderStateProductSelectionState,
  updateAdderStateProductId,
  mountDefaultTariffs,
  setTariffVerificationsLoading,
  setTariffVerifications,
  updateTariffVerificationValues,
  setHourSeatMapValidationStatus,
  setHourSeatMapValidationResult,
  setHourSeatMapTariffHash,
  clearHourSeatMapValidation,
  setRepricingStatus,
  receiveRepricedSchedulings,
  applyRepricedToTariffs,
} = productSlice.actions;

export const repricingByTariffIdSelector =
  (widgetId: string) =>
  (state: RootState): Record<string, RepricingStatus> =>
    state.product.repricingByWidget[widgetId]?.statusByTariffId ?? {};

export const repricingAppliedHashSelector =
  (widgetId: string) =>
  (state: RootState): string =>
    state.product.repricingByWidget[widgetId]?.appliedHash ?? '';

export const repricingStatusSelector =
  (widgetId: string) =>
  (state: RootState): RepricingStatus => {
    const byId = state.product.repricingByWidget[widgetId]?.statusByTariffId ?? {};
    const vals = Object.values(byId) as RepricingStatus[];
    if (vals.some((s) => s === 'loading')) return 'loading';
    if (vals.some((s) => s === 'error')) return 'error';
    return 'idle';
  };

export const isRepricingSelector =
  (widgetId: string) =>
  (state: RootState): boolean =>
    repricingStatusSelector(widgetId)(state) === 'loading';

export const hasRepricingErrorSelector =
  (widgetId: string) =>
  (state: RootState): boolean =>
    repricingStatusSelector(widgetId)(state) === 'error';

export const pricingTariffGatedSelector =
  (widgetId: string) =>
  (state: RootState): boolean => {
    const { listOfTariffsPerType } = tariffsState(widgetId)(state);
    return listOfTariffsPerType.some((t) => t.schedulings.some((s) => tariffsRequireQuantityPricing(s)));
  };

export const pricingTariffSelectionsHashSelector =
  (widgetId: string) =>
  (state: RootState): string => {
    const { listOfTariffsPerType } = tariffsState(widgetId)(state);
    const gated = listOfTariffsPerType.some((t) => t.schedulings.some((s) => tariffsRequireQuantityPricing(s)));
    const anySelected = listOfTariffsPerType.some((t) => t.selectedQuantity > 0);
    return gated && anySelected ? serializePricingTariffSelections(listOfTariffsPerType) : '';
  };

export const loadProduct = (id: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  if (state.product.products[id] || state.product.loadingProducts[id]) {
    return;
  }

  dispatch(setProductLoad(id, true));

  const client = getClient(state);
  try {
    const { data: product } = await client.products.getProduct(id);
    dispatch(setProduct({ ...product, additionals: [] }));

    const pageSize = 100;
    const additionals = [];

    for (let offset = 0, total = pageSize; offset < total; offset += pageSize) {
      const response = await client.products.getProductAdditionals(id, offset, pageSize);

      additionals.push(...response.data);
      total = getPaginationTotalCount(response);
    }

    dispatch(setProduct({ ...product, additionals }));

    if (product.sellingMode === SellingMode.WithoutDate) {
      dispatch(loadDefaultTariffGroup(id));
    }
  } finally {
    dispatch(setProductLoad(id, false));
  }
};

const loadProductAndInitializeAdderState =
  (id: string, productId: string, packageId = '') =>
  async (dispatch: AppDispatch) => {
    if (productLoadTimeouts[id]) {
      clearTimeout(productLoadTimeouts[id]);
    }

    productLoadTimeouts[id] = setTimeout(() => {
      dispatch(loadProduct(productId));
    }, 100);

    dispatch(initializeAdderState(id, productId, packageId));
  };

export const loadAdderState =
  (id: string, productId?: string, packageId = '', shouldSelectFirstProductAutomatically?: boolean) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const adderState = getState().product.adderState[id];

    if (!adderState) {
      if (productId) {
        dispatch(loadProductAndInitializeAdderState(id, productId, packageId));
        return;
      }

      if (packageId) {
        dispatch(setupProductPackage(id, packageId, shouldSelectFirstProductAutomatically));
      }
    }
  };

export const resetAdderState =
  (id: string, productId: string, packageId = '') =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const { highlightedProductAdder: hpa } = getState().product;

    if (packageId) {
      dispatch(resetAdderStateWithPackage(id, packageId));
    } else {
      dispatch(resetAdderStateWithProduct(id, productId));
    }

    dispatch(clearHourSeatMapValidation(id));

    if (hpa) {
      dispatch(setHighlightedProductAdder(hpa.id, hpa.params));
    }
  };

export const adderState = (id: string) => (state: RootState) => {
  return state.product.adderState[id] || initialProductAdderState('');
};

export const updateProductAdditional = (id: string, state: ProductAdditionalState) => (dispatch: AppDispatch) => {
  dispatch(productSlice.actions.updateProductAdditional({ id, value: state }));
};

export const loadDefaultTariffGroup =
  (productId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const {
      product: { productSchedules },
    } = state;

    const key = 'defaultTariffGroup';
    if (productSchedules[productId] && productSchedules[productId]!.loadedKeys[key]) {
      return;
    }

    dispatch(requestTariffGroup(productId));

    let response: AxiosResponse<TariffGroup>;
    try {
      response = (await getClient(state).products.getProductDefaultTariffGroup(
        productId,
      )) as AxiosResponse<TariffGroup>;
    } finally {
      dispatch(finishRequest(productId));
    }

    const { data: tariffGroup } = response;
    dispatch(receiveTariffGroup(productId, key, tariffGroup));
  };

export const loadAvailableDates =
  (productId: string, fromDate: string, toDate: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const {
      product: { productSchedules },
    } = state;

    // Without-date products have no calendar; never fetch schedulings for them. The
    // DateSelector also gates this client-side (and isn't even rendered for these products),
    // but the thunk owns the rule so any caller stays safe.
    if (state.product.products[productId]?.sellingMode === SellingMode.WithoutDate) {
      return;
    }

    const key = [fromDate, toDate].join('-');
    if (productSchedules[productId] && productSchedules[productId]!.loadedKeys[key]) {
      return;
    }

    dispatch(requestAvailableDates(productId));

    const client = getClient(state);
    const schedulings: Scheduling[] = [];
    const pageSize = 100;

    try {
      for (let offset = 0, total = pageSize; offset < total; offset += pageSize) {
        const resp = await client.products.getProductSchedulings(productId, fromDate, toDate, offset, pageSize);

        schedulings.push(...resp.data);
        total = getPaginationTotalCount(resp);
      }

      dispatch(receiveAvailableDates(productId, key, schedulings));
    } catch (error) {
      // The fetch failed mid-pagination: still record this range as settled (keeping any
      // pages that already succeeded) so consumers gating on `loadedKeys` — e.g.
      // `useUnitDisplayPrice` — fall back to the default tariff group instead of hanging
      // on a price skeleton forever, without discarding partial results.
      dispatch(receiveAvailableDates(productId, key, schedulings));
      throw error;
    } finally {
      dispatch(finishRequest(productId));
    }
  };

export const loadTariffGroupAttributes =
  (productId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();

    if (state.product.tariffGroupAttributes[productId] || state.product.loadingTariffGroupAttributes[productId]) {
      return;
    }

    dispatch(setTariffGroupAttributesLoading(productId, true));

    const client = getClient(state);
    const tariffGroupAttributes: TariffGroupAttribute[] = [];
    const pageSize = 100;

    try {
      for (let offset = 0, total = pageSize; offset < total; offset += pageSize) {
        const resp = await client.tariffGroupAttributes.getProductTariffGroupAttributes(productId, offset, pageSize);

        tariffGroupAttributes.push(...resp.data);
        total = getPaginationTotalCount(resp);
      }
    } finally {
      dispatch(setTariffGroupAttributesLoading(productId, false));
    }

    dispatch(setTariffGroupAttributes(productId, tariffGroupAttributes));
  };

export const loadTariffVerifications =
  (tariffTypeId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const {
      product: { tariffVerifications, loadingTariffVerifications },
    } = state;

    if (tariffVerifications[tariffTypeId] || loadingTariffVerifications[tariffTypeId]) {
      return;
    }

    dispatch(setTariffVerificationsLoading(tariffTypeId, true));

    const client = getClient(state);
    try {
      const response = await client.tariffTypeVerifications.getTariffTypeVerifications(tariffTypeId, 0, 100);
      dispatch(setTariffVerifications(tariffTypeId, response.data));
    } catch (error) {
      console.error('Error loading tariff verifications:', error);
      dispatch(setTariffVerifications(tariffTypeId, []));
    } finally {
      dispatch(setTariffVerificationsLoading(tariffTypeId, false));
    }
  };

const setStepIndexAddStep = (id: string, idx: number) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const steps = makeSteps(id, getState());
  dispatch(changeProductAddStep({ id, value: steps[idx]! }));
};

export const setStepNameAddStep =
  (id: string, name: ProductStepType) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const steps = makeSteps(id, getState());
    dispatch(changeProductAddStep({ id, value: steps.find((step) => step === name)! }));
  };

export const resetSteps = (id: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const steps = makeSteps(id, getState());

  if (steps.length) {
    dispatch(setStepIndexAddStep(id, 0));
  }
};

export const moveProductAddStep =
  (id: string, dir: number) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const { steps, currentStep } = productAdderStepState(id)(getState());
    const index = steps.indexOf(currentStep!);

    dispatch(setStepIndexAddStep(id, index + dir));
  };

export const previousProductAddStep = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(moveProductAddStep(id, -1));
};

export const addProductToCart = (id: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();

  const adderState = state.product.adderState[id]!;
  const productId = adderState.productId;

  const { selectedTariffHour, tariffVerificationValues, selectedAttributes } =
    adderState.productSelectionState[productId]!;
  const { selectedDate } = tariffsState(id)(state);
  const { selectedTariffs, selectedAdditionals, selectedResources } = adderResumeSelector(id)(state);

  const params = createAddToCartItemRequestPayload({
    productId,
    selectedTariffs,
    selectedDate,
    selectedTariffHour,
    selectedAdditionals,
    selectedResources,
    tariffVerificationValues,
    selectedAttributes,
  }) as AddCartItemRequest;

  const product = productSelector(productId)(state);

  track('productAdderProductAdded', {
    productId: product!.id,
    productName: product!.name,
    date: params.scheduleDate,
    time: params.scheduleTimeMinutes,
    additionals: params.additionals?.length ?? 0,
    resources: params.resources?.length ?? 0,
    tariffs: params.tariffs.reduce((acc, tariff) => acc + (tariff.quantity ?? 1), 0),
  });

  dispatch(addToCart(params, id));
};

export const goToNextStep = (id: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const { isAtLast } = productAdderStepState(id)(state);

  if (!isAtLast) {
    dispatch(moveProductAddStep(id, 1));
    return;
  }

  if (isSingleBookingPackageBookingFromState(id, state)) {
    dispatch(applySingleBookingSelectionAndCompleteBooking(id));
    return;
  }

  const booking = multiProductBookingSelector(state);
  if (booking?.isOpen && booking.widgetId === id) {
    const isPackage = isProductPackage(id)(state);

    if (!isPackage) {
      const currentProductId = adderState(id)(state).productId;
      const { totalCents } = adderResumeSelector(id, currentProductId)(state);
      trackUpsellBookingCompleted(booking.sourceProductId, currentProductId, totalCents);
    }

    dispatch(advanceMultiProductBooking(id, isPackage));
    return;
  }

  dispatch(addProductToCart(id));
};

export const createAddToCartItemRequestPayload = (props: AddToCartPayloadProps) => {
  const {
    productId,
    selectedAdditionals,
    selectedResources,
    selectedTariffs,
    selectedDate,
    selectedTariffHour,
    tariffVerificationValues,
    selectedAttributes,
  } = props;

  const minutes = selectedTariffHour?.minutes ?? null;
  const reqTariffs: AddCartItemTariff[] = [];

  for (const tariffPerType of selectedTariffs) {
    const tariff = getSelectedTariff(tariffPerType, minutes);
    if (tariff == null) {
      return;
    }

    const verificationValues = tariffVerificationValues?.find((t) => {
      return t.tariffTypeId === tariffPerType.type.id;
    })?.verificationValues;

    reqTariffs.push({
      tariffId: tariff.id,
      quantity: tariffPerType.selectedQuantity,
      verificationValues,
    });
  }

  const params: AddCartItemRequest = {
    productId,
    scheduleDate: selectedDate ?? null,
    scheduleTimeMinutes: minutes,
    tariffs: reqTariffs,
    additionals: selectedAdditionals.map((add) => ({
      additionalId: add.additional.id,
      inputValue: add.input,
      quantity: add.quantity,
    })),
    resources: [],
  };

  if (selectedResources.length) {
    params.resources = selectedResources.map((sr) => ({
      resourceId: sr.resourceSlot.id,
      tariffs: sr.tariffs.map((srt) => ({
        tariffId: srt.tariffId,
        quantity: srt.quantity,
      })),
    }));
  }

  if (selectedAttributes?.length) {
    // The generated `type` is narrowed to the single member the spec declares today; the wire
    // shape is identical for every type the backend adds. This is the only cast back.
    params.selectedAttributes = selectedAttributes as SelectedTariffGroupAttributes[];
  }

  return params;
};

export const checkTariffVerificationsIsValid = (
  listOfTariffsPerType: TariffsPerType[],
  tariffVerifications: { [tariffId: string]: TariffTypeVerification[] },
  tariffVerificationValues: TariffWithVerificationValues[],
): boolean => {
  return listOfTariffsPerType.every((tariffPerType) => {
    if (tariffPerType.selectedQuantity === 0) return true;

    const tariffTypeId = tariffPerType.type.id;
    const verifications = tariffVerifications[tariffTypeId] || [];

    const renderableVerifications = verifications.filter(
      (verification) =>
        verification.tariffVerification.type === TariffVerificationTypeEnum.Date ||
        verification.tariffVerification.type === TariffVerificationTypeEnum.Document,
    );

    if (renderableVerifications.length === 0) return true; // No verifications that need fields

    const verificationValuesForType =
      tariffVerificationValues.find((tariff) => tariff.tariffTypeId === tariffTypeId)?.verificationValues || [];
    const requiredFieldsCount = renderableVerifications.length * tariffPerType.selectedQuantity;
    const filledFieldsCount = verificationValuesForType.filter((verification) => verification.value.length > 0).length;

    return filledFieldsCount >= requiredFieldsCount;
  });
};

export const isProductAdderStepValid =
  (id: string, currentStep: ProductAdderValidationStep, options?: IsProductAdderStepValidOptions) =>
  (state: RootState): boolean => {
    const adderState = state.product.adderState[id]!;
    const { dates } = productScheduleSelector(adderState.productId)(state);

    const productId = adderState.productId;
    const currentProduct = adderState.productSelectionState[productId]!;

    switch (currentStep) {
      case 'calendar':
        return !!currentProduct.tariffsState.selectedDate;
      case 'tariff': {
        const {
          tariffsState: { listOfTariffsPerType },
        } = currentProduct;
        const selectedHour = currentProduct.selectedTariffHour;
        const product = state.product.products[productId]!;
        const isSingleBooking = isSingleBookingPackageBookingFromState(id, state);
        const hasSelectedATariff =
          listOfTariffsPerType.flatMap((tariff) => tariff.selectedQuantity).filter((quantity) => quantity).length > 0;
        const packageRequiresTime = isSingleBooking && packageRequiresTimeFromPackage(state, adderState.packageId);
        const hourIsValid =
          options?.skipTariffHourCheck ||
          (packageRequiresTime
            ? isPackageCommonHourSelectionValid(
                packageProductSchedulesSelector(adderState.packageId ?? '')(state),
                currentProduct.tariffsState.selectedDate,
                selectedHour,
              )
            : product?.sellingMode != SellingMode.WithDateAndTime || selectedHour);
        const isQtdSelectedValid =
          product.sellingMode != SellingMode.WithDateOnly || isTariffQtdSelectedAvailable(listOfTariffsPerType);
        const hasNotTariffDependencyError = !tariffDepedencyStatus(listOfTariffsPerType, selectedHour)
          .hasTariffDependencyError;
        const isSeatMapLoading = Object.values(state.product.loadingSchedulingSeatMap).some((loading) => loading);

        const allocationStatus = currentProduct.seatMapAllocationStatus;
        const allocationOk = options?.skipSeatMapAllocationCheck || allocationStatus.success;

        // Tariff verification validation
        const tariffVerificationsValid = checkTariffVerificationsIsValid(
          listOfTariffsPerType,
          state.product.tariffVerifications,
          currentProduct.tariffVerificationValues,
        );

        if (isSingleBooking) {
          return !!(hourIsValid && hasNotTariffDependencyError);
        }

        return !!(
          hasSelectedATariff &&
          hourIsValid &&
          isQtdSelectedValid &&
          hasNotTariffDependencyError &&
          allocationOk &&
          !isSeatMapLoading &&
          tariffVerificationsValid
        );
      }
      case 'package_quantity': {
        const {
          tariffsState: { listOfTariffsPerType },
        } = currentProduct;
        const product = state.product.products[productId]!;
        const selectedMinutes = currentProduct.selectedTariffHour?.minutes ?? null;
        const defaultTariffEntry = listOfTariffsPerType.find(
          (tariff) => getSelectedTariff(tariff, selectedMinutes)?.isDefault,
        );
        const quantity = defaultTariffEntry?.selectedQuantity ?? 0;

        if (isSingleBookingPackageBookingFromState(id, state)) {
          const packageProducts = state.product.packages[adderState.packageId ?? '']?.productPackageProducts ?? [];
          const currentProductTariffMax = defaultTariffEntry
            ? acceptableQuantities(
                defaultTariffEntry,
                currentProduct.tariffsState,
                product!,
                currentProduct.selectedTariffHour,
              )[1]
            : undefined;
          const { min, max } = resolvePackageSingleBookingQuantityLimits({
            products: packageProducts.map((item) =>
              mapPackageProductForSingleBookingQuantityLimits(
                item,
                state.product.products[item.productId]?.sellingMode ?? item.product?.sellingMode,
                {
                  dates: state.product.productSchedules[item.productId]?.dates,
                  defaultTariffGroup: state.product.productSchedules[item.productId]?.defaultTariffGroup,
                },
              ),
            ),
            selectedDate: currentProduct.tariffsState.selectedDate,
            selectedHour: currentProduct.selectedTariffHour,
            currentProductId: productId,
            currentProductTariffMax,
          });
          return Boolean(defaultTariffEntry) && max >= min && quantity >= min && quantity <= max;
        }

        return listOfTariffsPerType.some((tariff) => tariff.selectedQuantity > 0);
      }
      case 'seat_map':
        return currentProduct.resourcesAreOk;
      case 'additional': {
        const productAddditionals = currentProduct.productAdditionals;
        const additionalsCheckedWithInputEnabled = Object.values(productAddditionals).filter(
          (s) => s.checked && s.additional.enableInput,
        );

        const additionalsCheckedWithCustomQuantity = Object.values(productAddditionals).filter(
          (s) => s.checked && s.additional.type === AdditionalType.PerQuantity,
        );

        const additionalsAvailabilityAmount: Record<string, number | null> =
          additionalsCheckedWithCustomQuantity.reduce((acc, cur) => {
            return {
              ...acc,
              [cur.additional.id]: getAdditionalAvailability(
                dates[currentProduct.tariffsState.selectedDate ?? '']?.availables,
                cur.additional,
                state.product.products[productId]!,
                currentProduct.tariffsState.selectedDate,
                currentProduct.selectedTariffHour?.minutes,
                currentProduct.tariffsState.listOfTariffsPerType,
              ).availableAmount,
            };
          }, {});

        const isAdditionalQuantityValid =
          additionalsCheckedWithCustomQuantity.length === 0 ||
          additionalsCheckedWithCustomQuantity.every(
            (s) =>
              validateAdditionalQuantity({
                availableAmount: additionalsAvailabilityAmount[s.additional.id] ?? null,
                additional: s.additional,
                quantity: s.quantity,
              }).isQuantityValid,
          );

        const isInputEnabledValid = additionalsCheckedWithInputEnabled.filter((s) => !s.input.trim()).length == 0;

        return isInputEnabledValid && isAdditionalQuantityValid;
      }
      case 'time': {
        const product = state.product.products[productId];

        if (!product || product.sellingMode !== SellingMode.WithDateAndTime) {
          return true;
        }

        return Boolean(currentProduct.selectedTariffHour);
      }
      default:
        return true;
    }
  };

export const isCurrentStepValid =
  (id: string) =>
  (state: RootState): boolean => {
    const { currentStep } = productAdderStepState(id)(state);

    if (!currentStep || currentStep === 'finish') {
      return true;
    }

    return isProductAdderStepValid(id, currentStep)(state);
  };

export const loadSeatMap =
  (schedulingIdOrScheduling: string | Scheduling, timeMinutes?: number, selections = '') =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const schedulingId =
      typeof schedulingIdOrScheduling === 'string' ? schedulingIdOrScheduling : schedulingIdOrScheduling.id;

    try {
      const pricingTariffSelections = selections ? selections.split(',') : undefined;
      dispatch(setSeatMap(schedulingId, timeMinutes, undefined, selections));
      dispatch(setSeatMapLoading(schedulingId, timeMinutes, selections, true));

      const { data: seatMap } = await getClient(getState()).seatMaps.getSchedulingSeatMapOverview(
        schedulingId,
        timeMinutes,
        pricingTariffSelections,
      );

      dispatch(setSeatMap(schedulingId, timeMinutes, seatMap, selections));
    } finally {
      dispatch(setSeatMapLoading(schedulingId, timeMinutes, selections, false));
    }
  };

const BATCH_SIZE = 2;

// Store AbortControllers per widgetId to cancel pending requests when tariffs change
const hourValidationAbortControllers = new Map<string, AbortController>();

const repricingAbortControllers = new Map<string, AbortController>();
const repricingRequestIds = new Map<string, number>();

// 3 attempts total; price preview tolerates transient 5xx before showing an error banner
const REPRICE_MAX_RETRIES = 3;
// FIFO budget per product — covers typical browsing across 5 distinct quantity combos
const MAX_REPRICED_CACHE_SIZE = 5;
const MAX_REPRICE_PAGINATION_TOTAL = 10000;
const REPRICE_RETRY_DELAY_MS = 1000;

export const loadRepricedSchedulings =
  (widgetId: string, productId: string, date: string, selections: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    if (!selections) {
      dispatch(applyRepricedToTariffs(widgetId, ''));
      dispatch(setRepricingStatus(widgetId, 'idle'));
      return;
    }

    if (getState().product.repricedSchedulings[productId]?.[selections]) {
      dispatch(applyRepricedToTariffs(widgetId, selections));
      dispatch(setRepricingStatus(widgetId, 'idle'));
      track('tariff_price_preview', { result: 'cache_hit' });
      return;
    }

    // Only abort and bump the request counter when we are actually going to make a
    // network request. Cache hits above must not cancel an in-flight fetch for
    // different selections (e.g. re-triggers caused by appliedHash updating).
    repricingAbortControllers.get(widgetId)?.abort();
    repricingRequestIds.set(widgetId, (repricingRequestIds.get(widgetId) ?? 0) + 1);

    const abortController = new AbortController();
    repricingAbortControllers.set(widgetId, abortController);

    const requestId = repricingRequestIds.get(widgetId)!;

    const listOfTariffsPerType =
      getState().product.adderState?.[widgetId]?.productSelectionState[productId]?.tariffsState.listOfTariffsPerType ??
      [];
    const gatedTariffIds = listOfTariffsPerType
      .filter((t) => t.schedulings.some((s) => tariffsRequireQuantityPricing(s)))
      .map((t) => getSelectedTariff(t, null)?.id)
      .filter((id): id is string => !!id);

    dispatch(setRepricingStatus(widgetId, 'loading', gatedTariffIds));

    const client = getClient(getState());
    const pageSize = 100;
    const pricingTariffSelections = selections.split(',');

    try {
      for (let attempt = 1; attempt <= REPRICE_MAX_RETRIES; attempt += 1) {
        try {
          const schedulings: Scheduling[] = [];
          for (let offset = 0, total = pageSize; offset < total; offset += pageSize) {
            const resp = await client.products.getProductSchedulings(
              productId,
              date,
              date,
              offset,
              pageSize,
              pricingTariffSelections,
              { signal: abortController.signal },
            );
            schedulings.push(...resp.data);
            total = Math.min(getPaginationTotalCount(resp), MAX_REPRICE_PAGINATION_TOTAL);
          }

          if (repricingRequestIds.get(widgetId) !== requestId) return;

          dispatch(receiveRepricedSchedulings(productId, selections, schedulings));
          dispatch(applyRepricedToTariffs(widgetId, selections));
          dispatch(setRepricingStatus(widgetId, 'idle', gatedTariffIds));
          track('tariff_price_preview', { result: 'success' });
          return;
        } catch (error) {
          if (abortController.signal.aborted) {
            // Only reset to idle if no newer request has taken ownership of this widget's status
            if (repricingRequestIds.get(widgetId) === requestId) {
              dispatch(setRepricingStatus(widgetId, 'idle', gatedTariffIds));
            }
            track('tariff_price_preview', { result: 'abort' });
            return;
          }

          const status = (error as { response?: { status?: number } }).response?.status;
          if (status === 422 || attempt === REPRICE_MAX_RETRIES) {
            dispatch(applyRepricedToTariffs(widgetId, ''));
            dispatch(setRepricingStatus(widgetId, 'error', gatedTariffIds));
            track('tariff_price_preview', { result: 'error' });
            return;
          }
          await new Promise((resolve) => setTimeout(resolve, REPRICE_RETRY_DELAY_MS));
        }
      }
    } finally {
      if (repricingAbortControllers.get(widgetId) === abortController) {
        repricingAbortControllers.delete(widgetId);
      }
      if (repricingRequestIds.get(widgetId) === requestId) {
        repricingRequestIds.delete(widgetId);
      }
    }
  };

export const loadSeatMapValidationForHours =
  (widgetId: string, productId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const adder = state.product.adderState[widgetId];
    if (!adder || !productId) return;

    const productSelectionState = adder.productSelectionState[productId];
    if (!productSelectionState) return;

    const { listOfTariffsPerType } = productSelectionState.tariffsState;
    const selectedTariffsPerType = listOfTariffsPerType.filter((t) => t.selectedQuantity > 0);

    const repricingSelections = listOfTariffsPerType.some((t) =>
      t.schedulings.some((s) => tariffsRequireQuantityPricing(s)),
    )
      ? serializePricingTariffSelections(listOfTariffsPerType)
      : '';

    const hasSchedulingRequiringSeat = hasSchedulingRequiringSeatSelection(selectedTariffsPerType);

    if (!hasSchedulingRequiringSeat || selectedTariffsPerType.length === 0) return;

    const scheduleTimes = getDistinctScheduleTimes(selectedTariffsPerType);

    if (scheduleTimes.length === 0) return;

    const tariffHash = createTariffHash(selectedTariffsPerType);
    const currentValidation = getState().product.hourSeatMapValidation[widgetId];

    if (currentValidation?.tariffHash === tariffHash && currentValidation?.status === 'completed') {
      return;
    }

    // Cancel any pending validation for this widget
    const previousController = hourValidationAbortControllers.get(widgetId);
    if (previousController) {
      previousController.abort();
    }

    // Create new AbortController for this validation run
    const abortController = new AbortController();
    hourValidationAbortControllers.set(widgetId, abortController);

    if (currentValidation?.tariffHash !== tariffHash) {
      dispatch(clearHourSeatMapValidation(widgetId));
    }

    dispatch(setHourSeatMapValidationStatus(widgetId, 'loading'));
    dispatch(setHourSeatMapTariffHash(widgetId, tariffHash));

    const client = getClient(getState());
    const limit = pLimit(BATCH_SIZE);

    const processScheduleTime = async (scheduleTime: ScheduleTime) => {
      if (abortController.signal.aborted) return;

      const scheduling = findSchedulingForTime(selectedTariffsPerType, scheduleTime.minutes);
      if (!scheduling) return;

      const key = formatSeatMapKey(scheduling.id, scheduleTime.minutes);

      try {
        const selectedTariffs = makeSelectedTariffs(selectedTariffsPerType, scheduleTime.minutes);

        const { data: seatMap } = await client.seatMaps.getSchedulingSeatMapOverview(
          scheduling.id,
          scheduleTime.minutes,
          repricingSelections ? repricingSelections.split(',') : undefined,
          { signal: abortController.signal },
        );

        if (abortController.signal.aborted) return;

        dispatch(setSeatMap(scheduling.id, scheduleTime.minutes, seatMap, repricingSelections));
        // Also write at the baseline key so useAllocationStatus and SeatsUnavailableAlert
        // find the cached overview without a redundant baseline fetch.
        if (repricingSelections) {
          dispatch(setSeatMap(scheduling.id, scheduleTime.minutes, seatMap, ''));
        }

        const allocationStatus = getSeatMapBestAllocationStatus(seatMap, selectedTariffs);
        dispatch(setHourSeatMapValidationResult(widgetId, key, allocationStatus));
      } catch (error) {
        if (abortController.signal.aborted) return;

        dispatch(
          setHourSeatMapValidationResult(widgetId, key, { success: false, missingCapacity: 0, missingTariffs: 0 }),
        );
      }
    };

    await Promise.all(scheduleTimes.map((st) => limit(() => processScheduleTime(st))));

    // Check if aborted before setting completed
    if (abortController.signal.aborted) return;

    // Set completed status and cleanup
    dispatch(setHourSeatMapValidationStatus(widgetId, 'completed'));
    hourValidationAbortControllers.delete(widgetId);
  };

export const productSelector = (id: string) => (state: RootState) => state.product.products[id];

export const additionalsSelector = (id: string, productId?: string) => (state: RootState) => {
  const { adderState, productId: currentProductId } = adderStateAndCurrentProductIdSelector(id)(state);
  const resolvedProductId = productId ?? currentProductId;

  return adderState.productSelectionState[resolvedProductId]?.productAdditionals;
};

export const productScheduleSelector =
  (id: string) =>
  (state: RootState): ProductScheduleState =>
    state.product.productSchedules[id] || {
      dates: {},
      loadedKeys: {},
      pending: true,
    };

export const schedulingSeatMapSelector =
  (schedulingId: string, timeMinutes?: number, selectionsHash = '') =>
  (state: RootState): SeatMapOverview | undefined =>
    state.product.seatMaps[formatSeatMapKey(schedulingId, timeMinutes, selectionsHash)];

export const seatMapSelector = () => (state: RootState) => state.product.seatMaps;

export const seatMapAllocationStatusSelector = (id: string, productId?: string) => (state: RootState) => {
  const { adderState, productId: currentProductId } = adderStateAndCurrentProductIdSelector(id)(state);
  const resolvedProductId = productId ?? currentProductId;

  const selection = resolveProductSelection(adderState, resolvedProductId);
  return selection.seatMapAllocationStatus;
};

export const schedulingSeatMapLoadingSelector =
  (schedulingId: string, timeMinutes?: number, selectionsHash = '') =>
  (state: RootState) =>
    state.product.loadingSchedulingSeatMap[formatSeatMapKey(schedulingId, timeMinutes, selectionsHash)];

const isSingleBookingPackageBookingFromState = (widgetId: string, state: RootState): boolean => {
  const packageId = state.product.adderState[widgetId]?.packageId;
  if (!packageId) {
    return false;
  }

  const configurationMode = state.product.packages[packageId]?.productPackage?.configurationMode;
  const isFlagEnabled = state.app.appInfo?.featureFlags?.['PR-4664-product-package-single-journey'] === true;

  return shouldUseSingleBookingPackageBooking(configurationMode, isFlagEnabled);
};

const packageRequiresTimeFromPackage = (state: RootState, packageId: string | undefined): boolean => {
  if (!packageId) {
    return false;
  }

  const products = state.product.packages[packageId]?.productPackageProducts ?? [];
  return packageRequiresTimeStep(products.map((item) => item.product?.sellingMode));
};

export const makeSteps = (widgetId: string, state: RootState): ProductStepType[] => {
  const addState = adderState(widgetId)(state);

  if (isSingleBookingPackageBookingFromState(widgetId, state) && addState.packageId) {
    const packageProducts = state.product.packages[addState.packageId]?.productPackageProducts ?? [];
    return decidePackageBookingSteps(packageProducts.map((item) => item.product?.sellingMode));
  }

  const product = productSelector(addState.productId)(state);

  if (!product) {
    return [];
  }

  const isWithoutDate = product!.sellingMode === SellingMode.WithoutDate;
  const steps: ProductStepType[] = [];
  if (!isWithoutDate) {
    steps.push('calendar');
  }

  steps.push('tariff');

  const { listOfTariffsPerType } = tariffsState(widgetId)(state);
  const selectedHour = selectedProductTariffHour(widgetId)(state);
  const selectedMinutes = selectedHour?.minutes ?? null;
  const selectedScheduling = getSchedulingFrom(product!, listOfTariffsPerType, selectedMinutes);

  if (!isWithoutDate && selectedScheduling?.requiresSeatSelection) {
    steps.push('seat_map');
  }

  if (product.additionals.length) {
    steps.push('additional');
  }

  steps.push('finish');

  return steps;
};

export const productAdderStepState = (id: string, productId?: string) => (state: RootState) => {
  const { adderState, productId: currentProductId } = adderStateAndCurrentProductIdSelector(id)(state);
  const resolvedProductId = productId ?? currentProductId;

  if (!resolvedProductId) {
    return { steps: [], currentStep: undefined, isAtLast: false };
  }

  const selection = resolveProductSelection(adderState, resolvedProductId);
  let currentStep = selection.currentProductAdderStep;

  const steps = makeSteps(id, state);

  if (currentStep == null && steps.length) {
    currentStep = steps[0];
  }

  const validSteps = steps.filter((s) => s !== 'finish');
  const isAtLast = currentStep == validSteps[validSteps.length - 1];

  return { steps, currentStep, isAtLast };
};

// Cache selector instances per (widgetId, productId) so each component reuses the same
// memoized selector — required for createSelector to avoid recomputing on every store update.
const tariffsStateSelectorCache = new Map<string, (state: RootState) => TariffsState>();
const MAX_TARIFFS_STATE_SELECTOR_CACHE_SIZE = 20;

export const tariffsState = (id: string, productId?: string): ((state: RootState) => TariffsState) => {
  const cacheKey = `${id}:${productId ?? ''}`;
  const cachedSelector = tariffsStateSelectorCache.get(cacheKey);
  if (cachedSelector) {
    tariffsStateSelectorCache.delete(cacheKey);
    tariffsStateSelectorCache.set(cacheKey, cachedSelector);
    return cachedSelector;
  }

  const selector = createSelector(
    (state: RootState) => adderStateAndCurrentProductIdSelector(id)(state),
    (state: RootState) => state.product.repricingByWidget?.[id]?.appliedHash ?? '',
    (state: RootState) => state.product.repricedSchedulings ?? {},
    ({ adderState, productId: currentProductId }, appliedHash, allRepricedSchedulings) => {
      const resolvedProductId = productId ?? currentProductId;

      if (!resolvedProductId) return { listOfTariffsPerType: [], selectedDate: undefined } as TariffsState;

      const productSelection = adderState.productSelectionState[resolvedProductId];
      if (!productSelection) return { listOfTariffsPerType: [], selectedDate: undefined } as TariffsState;

      const { selectedDate, listOfTariffsPerType } = productSelection.tariffsState;

      if (appliedHash) {
        const repricedSchedulings = allRepricedSchedulings[resolvedProductId]?.[appliedHash];
        if (repricedSchedulings) {
          return {
            selectedDate,
            listOfTariffsPerType: listOfTariffsPerType.map((t) => ({
              ...t,
              schedulings: overlayRepricedPrices(t.schedulings, repricedSchedulings),
            })),
          };
        }
      }

      return { listOfTariffsPerType, selectedDate };
    },
  );

  tariffsStateSelectorCache.set(cacheKey, selector);
  if (tariffsStateSelectorCache.size > MAX_TARIFFS_STATE_SELECTOR_CACHE_SIZE) {
    const oldestKey = tariffsStateSelectorCache.keys().next().value;
    if (oldestKey !== undefined) tariffsStateSelectorCache.delete(oldestKey);
  }

  return selector;
};

export const selectedProductTariffHour = (id: string, productId?: string) => (state: RootState) => {
  const { adderState, productId: currentProductId } = adderStateAndCurrentProductIdSelector(id)(state);
  const resolvedProductId = productId ?? currentProductId;

  return adderState.productSelectionState[resolvedProductId]?.selectedTariffHour;
};

export const selectedProductAttributesSelector = (id: string, productId?: string) => (state: RootState) => {
  const { adderState, productId: currentProductId } = adderStateAndCurrentProductIdSelector(id)(state);
  const resolvedProductId = productId ?? currentProductId;

  if (!resolvedProductId) {
    return [];
  }

  return adderState.productSelectionState[resolvedProductId]?.selectedAttributes ?? [];
};

export const loadingTariffGroupAttributesSelector = (productId: string) => (state: RootState) =>
  Boolean(state.product.loadingTariffGroupAttributes[productId]);

const EMPTY_LANGUAGE_OPTIONS: LanguageOption[] = [];
const EMPTY_ATTRIBUTE_SLOTS: AttributeOptionGroup[] = [];

const attributeSlotsSelectorCache = new Map<string, (state: RootState) => AttributeOptionGroup[]>();
const MAX_ATTRIBUTE_SLOTS_SELECTOR_CACHE_SIZE = 20;

const currentSelectionKeys = (widgetId: string, state: RootState) => {
  const adder = state.product.adderState[widgetId];
  const productId = adder?.productId;
  const selectedDate = productId ? adder!.productSelectionState[productId]?.tariffsState.selectedDate : undefined;

  return { productId, selectedDate };
};

/**
 * Every attribute selector the current (product, date, selection) offers, in slot order and
 * already cascaded — see `buildAttributeSlotsForSchedulings`.
 *
 * Derived instead of cached in the store: the cascade depends on what this widget has selected,
 * so a per-date cache in `ProductState` could not represent it. Memoized per widget so the
 * returned array keeps a stable reference across renders.
 */
export const attributeSlotsSelector = (widgetId: string): ((state: RootState) => AttributeOptionGroup[]) => {
  const cachedSelector = attributeSlotsSelectorCache.get(widgetId);
  if (cachedSelector) {
    attributeSlotsSelectorCache.delete(widgetId);
    attributeSlotsSelectorCache.set(widgetId, cachedSelector);
    return cachedSelector;
  }

  const selector = createSelector(
    (state: RootState) => {
      const { productId } = currentSelectionKeys(widgetId, state);
      return productId ? state.product.tariffGroupAttributes[productId] : undefined;
    },
    (state: RootState) => {
      const { productId, selectedDate } = currentSelectionKeys(widgetId, state);
      if (!productId || !selectedDate) return undefined;
      return resolveSchedulingsForDate(state.product.productSchedules[productId]?.dates, selectedDate)?.availables;
    },
    (state: RootState) => {
      const { productId } = currentSelectionKeys(widgetId, state);
      if (!productId) return undefined;
      return state.product.adderState[widgetId]?.productSelectionState[productId]?.selectedAttributes;
    },
    (attributes, availables, selectedAttributes) => {
      if (!attributes?.length || !availables?.length) {
        return EMPTY_ATTRIBUTE_SLOTS;
      }

      const slots = buildAttributeSlotsForSchedulings(attributes, availables, selectedAttributes ?? []);

      return slots.length ? slots : EMPTY_ATTRIBUTE_SLOTS;
    },
  );

  attributeSlotsSelectorCache.set(widgetId, selector);
  if (attributeSlotsSelectorCache.size > MAX_ATTRIBUTE_SLOTS_SELECTOR_CACHE_SIZE) {
    const oldestKey = attributeSlotsSelectorCache.keys().next().value;
    if (oldestKey !== undefined) attributeSlotsSelectorCache.delete(oldestKey);
  }

  return selector;
};

export const attributeSlotSelector =
  (widgetId: string, type: TariffGroupAttributeType) =>
  (state: RootState): AttributeOptionGroup | undefined =>
    attributeSlotsSelector(widgetId)(state).find((slot) => slot.type === type);

export const languageOptionsSelector = (widgetId: string) => (state: RootState) => {
  // The `language` bucket is always produced by `buildLanguageOptions`, which fills `flagCode`.
  // Returned by reference so the memoized slot array keeps consumers from re-rendering.
  const options = attributeSlotSelector(widgetId, LANGUAGE_ATTRIBUTE_TYPE)(state)?.options;

  return (options as LanguageOption[] | undefined) ?? EMPTY_LANGUAGE_OPTIONS;
};

export const productHasLanguageStepSelector = (productId: string) => (state: RootState) =>
  (state.product.tariffGroupAttributes[productId] ?? []).some(
    (attribute) => isActiveAttribute(attribute) && isLanguageAttributeType(attribute.type),
  );

const EMPTY_ATTRIBUTE_TYPES: TariffGroupAttributeType[] = [];
const productAttributeTypesCache = new Map<string, (state: RootState) => TariffGroupAttributeType[]>();
const MAX_PRODUCT_ATTRIBUTE_TYPES_CACHE_SIZE = 20;

/**
 * Non-language attribute types the product declares, in slot order. Date-independent on purpose:
 * this drives which slots the embed mounts, mirroring how `productHasLanguageStepSelector` drives
 * the language slot. Whether a slot actually has options for the chosen date is decided later, by
 * `attributeSlotsSelector`.
 */
export const productAttributeTypesSelector = (
  productId: string,
): ((state: RootState) => TariffGroupAttributeType[]) => {
  const cachedSelector = productAttributeTypesCache.get(productId);
  if (cachedSelector) {
    productAttributeTypesCache.delete(productId);
    productAttributeTypesCache.set(productId, cachedSelector);
    return cachedSelector;
  }

  const selector = createSelector(
    (state: RootState) => state.product.tariffGroupAttributes[productId],
    (attributes) => {
      if (!attributes?.length) {
        return EMPTY_ATTRIBUTE_TYPES;
      }

      const types = orderedAttributeTypes(attributes).filter((type) => !isLanguageAttributeType(type));

      return types.length ? types : EMPTY_ATTRIBUTE_TYPES;
    },
  );

  productAttributeTypesCache.set(productId, selector);
  if (productAttributeTypesCache.size > MAX_PRODUCT_ATTRIBUTE_TYPES_CACHE_SIZE) {
    const oldestKey = productAttributeTypesCache.keys().next().value;
    if (oldestKey !== undefined) productAttributeTypesCache.delete(oldestKey);
  }

  return selector;
};

export const tariffGroupAttributesLoadedSelector = (productId: string) => (state: RootState) =>
  state.product.tariffGroupAttributes[productId] !== undefined;

export const selectedTariffsSelector = (id: string, productId?: string) => (state: RootState) => {
  const { adderState, productId: currentProductId } = adderStateAndCurrentProductIdSelector(id)(state);
  const resolvedProductId = productId ?? currentProductId;

  if (!resolvedProductId) {
    return [];
  }

  const selection = resolveProductSelection(adderState, resolvedProductId);
  return selection.tariffsState.listOfTariffsPerType.filter((tariff) => tariff.selectedQuantity);
};

export const selectedProductResources = (id: string, productId?: string) => (state: RootState) => {
  const { adderState, productId: currentProductId } = adderStateAndCurrentProductIdSelector(id)(state);
  const resolvedProductId = productId ?? currentProductId;

  const selection = resolveProductSelection(adderState, resolvedProductId);
  return selection.selectedResources;
};

export const selectedAdditionalsSelector = (id: string, productId?: string) => (state: RootState) => {
  const { adderState, productId: currentProductId } = adderStateAndCurrentProductIdSelector(id)(state);
  const resolvedProductId = productId ?? currentProductId;

  if (!resolvedProductId) {
    return [];
  }

  const productAdditionals = adderState.productSelectionState[resolvedProductId]?.productAdditionals;
  if (!productAdditionals) {
    return [];
  }

  return Object.values(productAdditionals).filter((s) => s.checked);
};

export const checkedAdditionalsCountSelector = (id: string, productId?: string) => (state: RootState) => {
  return selectedAdditionalsSelector(id, productId)(state).length;
};

export const currentProductAdderStatus = (id: string) => (state: RootState) => adderState(id)(state).currentStatus;

export const getExceedingValueCents = (id: string, productId?: string) => (state: RootState) => {
  const { adderState, productId: currentProductId } = adderStateAndCurrentProductIdSelector(id)(state);
  const resolvedProductId = productId ?? currentProductId;

  const selection = resolveProductSelection(adderState, resolvedProductId);
  return selection.exceedingValueCents;
};

export const productAdditionalFormInput =
  (widgetId: string, additional: Additional, productId?: string) => (state: RootState) => {
    const { adderState, productId: currentProductId } = adderStateAndCurrentProductIdSelector(widgetId)(state);
    const resolvedProductId = productId ?? currentProductId;
    const emptyStateAdditional = { additional, input: '', checked: false, quantity: undefined };

    if (!resolvedProductId) {
      return emptyStateAdditional;
    }

    const stateAdditional = adderState.productSelectionState[resolvedProductId]?.productAdditionals[additional.id];

    return stateAdditional ?? emptyStateAdditional;
  };

export const additionalsSelectionIsCompleteSelector = (widgetId: string, productId: string) => (state: RootState) => {
  const product = productSelector(productId)(state);
  const tariffsWidget = tariffsState(widgetId, productId)(state);

  return additionalsSelectionIsComplete({
    additionals: product?.additionals ?? [],
    product,
    dates: productScheduleSelector(productId)(state).dates,
    selectedDate: tariffsWidget.selectedDate,
    selectedTariffMinutes: selectedProductTariffHour(widgetId, productId)(state)?.minutes,
    listOfTariffsPerType: tariffsWidget.listOfTariffsPerType,
    getAdditionalFormInput: (additional) => productAdditionalFormInput(widgetId, additional, productId)(state),
  });
};

export const adderResumeSelector = (id: string, prodId?: string) => (state: RootState) => {
  const { adderState: widgetAdderState, productId: currentProductId } =
    adderStateAndCurrentProductIdSelector(id)(state);

  const productId = prodId ?? currentProductId;

  if (!productId)
    return {
      selectedTariffHour: undefined,
      productId: '',
      selectedTariffs: [],
      selectedDate: undefined,
      tariffsPriceCents: 0,
      selectedAdditionals: [],
      additionalsPriceCents: 0,
      selectedResources: [],
      resourcesPriceCents: 0,
      totalCents: 0,
    };

  const selectedTariffs = selectedTariffsSelector(id, productId)(state);
  const selectedAdditionals = selectedAdditionalsSelector(id, productId)(state);

  const { selectedTariffHour, exceedingValueCents, selectedResources } = resolveProductSelection(
    widgetAdderState,
    productId,
  );

  const { listOfTariffsPerType, selectedDate } = tariffsState(id, productId)(state);

  const tariffsResume = listOfTariffsPerType
    .flatMap((t) => t)
    .reduce(
      ({ price, quantity }, tariffsPerType) => {
        price += getTariffPriceInformation(tariffsPerType, selectedTariffHour ?? null);
        quantity += tariffsPerType.selectedQuantity;

        return { price, quantity };
      },
      { price: 0, quantity: 0 },
    );

  const additionalsPriceCents = selectedAdditionals.reduce(
    (sum, s) => sum + (getAdditionalPrice(s.additional, listOfTariffsPerType, s.quantity) ?? 0),
    0,
  );

  return {
    selectedTariffHour,
    productId,
    selectedTariffs,
    selectedDate,
    tariffsPriceCents: tariffsResume.price,
    selectedAdditionals,
    additionalsPriceCents,
    selectedResources,
    resourcesPriceCents: exceedingValueCents,
    totalCents: tariffsResume.price + exceedingValueCents + additionalsPriceCents,
  };
};

export const highlightedProductAdderSelector = (state: RootState) => state.product.highlightedProductAdder;

export const loadingProductSelector = (id: string) => (state: RootState) => state.product.loadingProducts[id];

export const productAdderSelectedProduct = (id: string) => (state: RootState) => adderState(id)(state).productId;

export const packageSelector = (packageId: string) => (state: RootState) => state.product.packages[packageId];

export const updatePackage =
  (packageId: string, packageState: PackageState) => (dispatch: AppDispatch, getState: () => RootState) => {
    const currentPackageState = packageSelector(packageId)(getState());

    if (currentPackageState) {
      dispatch(setPackage(packageId, { ...currentPackageState, ...packageState }));
    } else {
      dispatch(setPackage(packageId, packageState));
    }
  };

const resetAdderStateWithProduct = (widgetId: string, productId: string) => (dispatch: AppDispatch) => {
  dispatch(initializeAdderState(widgetId, productId, ''));
};

const resetAdderStateWithPackage =
  (widgetId: string, packageId: string) => (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const productPackageProducts = productPackageProductsSelector(packageId)(state)!;
    const autoSelectProducts = adderState(widgetId)(state).autoSelectProducts;

    if (productPackageProducts.length) {
      dispatch(startAdderStateWithPackage(widgetId, packageId, productPackageProducts, autoSelectProducts));
    }

    dispatch(updatePackage(packageId, { status: 'loaded', error: undefined }));
  };

const startAdderStateWithPackage =
  (widgetId: string, packageId: string, productPackageProducts: ProductPackageProduct[], shouldAutoSelect?: boolean) =>
  async (dispatch: AppDispatch) => {
    const productId = shouldAutoSelect ? productPackageProducts[0]!.productId : '';
    const productSelectionState = productPackageProducts.reduce((acc, ppp) => {
      acc[ppp.productId] = createInitialProductSelectionState();
      return acc;
    }, {} as Record<string, ProductSelectionState>);

    dispatch(loadProductAndInitializeAdderState(widgetId, productId, packageId));

    if (shouldAutoSelect) {
      dispatch(updatePackage(packageId, { currentProductIndexAtAdder: 0 }));
      dispatch(updateAdderStateAutoSelectProducts({ id: widgetId, value: true }));
    }

    dispatch(updateAdderStateProductSelectionState({ id: widgetId, value: productSelectionState }));

    if (productId) {
      dispatch(mountDefaultTariffs(productId));
    }
  };

export const setupProductPackage =
  (widgetId: string, packageId: string, shouldSelectFirstProductAutomatically?: boolean) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(updatePackage(packageId, { status: 'loading' }));

    await Promise.all([
      dispatch(loadProductPackage(packageId)),
      dispatch(loadProductPackageProducts(widgetId, packageId, shouldSelectFirstProductAutomatically)),
    ]);

    const state = getState();
    if (!isSingleBookingPackageBookingFromState(widgetId, state)) {
      return;
    }

    const packageProducts = productPackageProductsSelector(packageId)(state) ?? [];
    const selectedProductId = adderState(widgetId)(state).productId;
    if (!selectedProductId && packageProducts.length) {
      const firstProductId = pickFirstScheduledProductId(packageProducts);
      if (firstProductId) {
        dispatch(addNextProductPackageToAdderState(widgetId, firstProductId));
      }
    }

    dispatch(preloadSingleBookingPackageData(widgetId, packageId));
  };

const triggerError = (packageId: string, error: string) => (dispatch: AppDispatch) => {
  dispatch(updatePackage(packageId, { error }));
};

export const loadProductPackage = (packageId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
  try {
    const productPackage = await fetchProductPackage(packageId, getState());

    dispatch(updatePackage(packageId, { productPackage }));
  } catch (error: any) {
    dispatch(triggerError(packageId, 'product_package_error_while_load_package_data'));
  }
};

const fetchProductPackage = async (packageId: string, rootState: RootState) => {
  const { data } = await getClient(rootState).productPackage.getProductPackage(packageId);

  return data;
};

export const loadProductPackageProducts =
  (widgetId: string, packageId: string, shouldAutoSelect?: boolean) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();

    try {
      const productPackageProducts = await fetchProductPackageProducts(packageId, state);

      if (isProductPackageProductsEmpty(productPackageProducts)) {
        dispatch(triggerError(packageId, 'product_package_error_empty_package'));
        return;
      }

      dispatch(updatePackage(packageId, { productPackageProducts }));

      const hasInactiveProduct = checkIfThePackageHasInactiveProductSelector(packageId)(state);

      if (hasInactiveProduct) {
        dispatch(triggerError(packageId, 'product_package_error_has_inactive_product'));

        return;
      }

      dispatch(startAdderStateWithPackage(widgetId, packageId, productPackageProducts, shouldAutoSelect));
    } catch (error: any) {
      dispatch(triggerError(packageId, 'product_package_error_while_load_package_products_data'));
    } finally {
      dispatch(updatePackage(packageId, { status: 'loaded' }));
    }
  };

const fetchProductPackageProducts = async (packageId: string, rootState: RootState) => {
  const { data } = await getClient(rootState).productPackage.getProductPackageProducts(packageId);

  return data;
};

const isProductPackageProductsEmpty = (productPackageProducts: ProductPackageProduct[]) =>
  !productPackageProducts || productPackageProducts.length === 0;

export const addNextProductPackageToAdderState =
  (widgetId: string, productId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();

    const packageId = packageIdSelector(widgetId)(state)!;
    const productPackageProducts = productPackageProductsSelector(packageId)(state)!;

    const { productSelectionState } = adderState(widgetId)(state);

    if (productSelectionState[productId]?.currentProductAdderStep === 'finish') return;

    const productIndex = productPackageProducts!.findIndex((ppp) => ppp.productId === productId);

    dispatch(resetSelectedProduct(widgetId, productId));

    dispatch(loadProduct(productId));

    // loadProduct bails when the product is already cached (reopen), so setProduct/receiveTariffGroup
    // won't fire again to mount without-date tariffs. Mount explicitly: no-op for date-based products,
    // when the default tariff group isn't loaded yet, or when tariffs are already mounted.
    dispatch(mountDefaultTariffs(productId));

    // This reducer also runs for multi_booking packages (PackageBookingModal's product picker),
    // so only clear the single-booking-flow error here — otherwise selecting a product would
    // silently wipe product_package_error_has_inactive_product / _empty_package, which
    // triggerError set for the whole package during load and updatePackage's shallow merge would
    // then drop.
    const currentError = productPackageErrorSelector(packageId)(state);
    dispatch(
      updatePackage(packageId, {
        status: 'loaded',
        currentProductIndexAtAdder: productIndex,
        ...(currentError === 'product_package_single_booking_unavailable' ? { error: undefined } : {}),
      }),
    );
  };

export const resetSelectedProduct =
  (widgetId: string, productId = '') =>
  async (dispatch: AppDispatch) => {
    dispatch(updateAdderStateProductId({ id: widgetId, value: productId }));
  };

export const preloadSingleBookingPackageData =
  (_widgetId: string, packageId: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    const packageProducts = productPackageProductsSelector(packageId)(getState()) ?? [];

    await Promise.all(packageProducts.map((item) => dispatch(loadProduct(item.productId))));

    const state = getState();
    const range = computeVisibleRange(initialMonth());

    await Promise.all(
      packageProducts.map((item) => {
        const product = state.product.products[item.productId];
        if (product?.sellingMode === SellingMode.WithoutDate) {
          return dispatch(loadDefaultTariffGroup(item.productId));
        }
        return dispatch(loadAvailableDates(item.productId, range.dateFrom, range.dateTo));
      }),
    );
  };

export const applySingleBookingSelectionAndCompleteBooking =
  (widgetId: string) => (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState();
    const packageId = packageIdSelector(widgetId)(state);
    if (!packageId) {
      return;
    }

    dispatch(updatePackage(packageId, { error: undefined }));

    const { productSelectionState, productId: currentProductId } = adderState(widgetId)(state);
    const currentSelection = productSelectionState[currentProductId];
    const selectedDate = currentSelection?.tariffsState.selectedDate;
    const selectedHour = currentSelection?.selectedTariffHour;
    const selectedMinutes = selectedHour?.minutes ?? null;
    const quantity =
      currentSelection?.tariffsState.listOfTariffsPerType.find((tariffPerType) => {
        const selected = getSelectedTariff(tariffPerType, selectedMinutes);
        return Boolean(selected?.isDefault) && tariffPerType.selectedQuantity > 0;
      })?.selectedQuantity ??
      currentSelection?.tariffsState.listOfTariffsPerType.find((tariffPerType) => tariffPerType.selectedQuantity > 0)
        ?.selectedQuantity ??
      0;

    const packageProducts = productPackageProductsSelector(packageId)(state) ?? [];
    const result = replicateSingleBookingSelection({
      date: selectedDate,
      time: selectedHour,
      quantity,
      products: packageProducts.map((item) => {
        const sellingMode = state.product.products[item.productId]?.sellingMode ?? item.product?.sellingMode;

        const availables = selectedDate
          ? resolveSchedulingsForDate(state.product.productSchedules[item.productId]?.dates, selectedDate)
              ?.availables ?? []
          : [];
        const availablesAtSelectedTime =
          sellingMode === SellingMode.WithDateAndTime && selectedMinutes != null
            ? availables.filter((scheduling) => schedulingContainsMinute(scheduling, selectedMinutes))
            : availables;
        const schedulingForDate =
          availablesAtSelectedTime.find((scheduling) => resolveDefaultTariffFromGroup(scheduling.tariffGroup)) ??
          availablesAtSelectedTime[0] ??
          null;

        return {
          productId: item.productId,
          sellingMode,
          schedulingForDate,
          defaultTariffGroup: state.product.productSchedules[item.productId]?.defaultTariffGroup,
        };
      }),
    });

    if (!result.ok) {
      dispatch(updatePackage(packageId, { error: 'product_package_single_booking_unavailable' }));
      return;
    }

    const productPackageProductIdByProductId = packageProducts.reduce<Record<string, string>>((acc, item) => {
      acc[item.productId] = item.id;
      return acc;
    }, {});

    const hasMissingProductPackageProductId = result.selections.some(
      (selection) => !productPackageProductIdByProductId[selection.productId],
    );
    if (hasMissingProductPackageProductId) {
      dispatch(updatePackage(packageId, { error: 'product_package_single_booking_unavailable' }));
      return;
    }

    const nextSelectionState = { ...productSelectionState };
    for (const selection of result.selections) {
      const current = nextSelectionState[selection.productId] ?? createInitialProductSelectionState();
      nextSelectionState[selection.productId] = {
        ...current,
        currentProductAdderStep: 'finish',
        selectedTariffHour: selection.time,
        tariffsState: {
          selectedDate: selection.date,
          listOfTariffsPerType: [
            {
              type: selection.tariff.type,
              selectedQuantity: selection.quantity,
              schedulings: selection.scheduling ? [selection.scheduling] : [],
              tariffGroup: selection.tariffGroup,
            },
          ],
        },
      };
    }

    dispatch(updateAdderStateProductSelectionState({ id: widgetId, value: nextSelectionState }));
    dispatch(advanceMultiProductBooking(widgetId, true));
  };

export const addPackageToCart = (widgetId: string) => (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();

  const packageId = packageIdSelector(widgetId)(state)!;
  const { productSelectionState } = adderState(widgetId)(state);
  const productPackageProducts = productPackageProductsSelector(packageId)(state)!;

  const mapProductIdToProductPackageProductId = productPackageProducts.reduce((acc, ppp) => {
    acc[ppp.productId] = ppp.id;
    return acc;
  }, {} as Record<string, string>);

  const items = Object.keys(productSelectionState).map((productId) => {
    const adderResume = adderResumeSelector(widgetId, productId)(state);
    const tariffVerificationValues = productSelectionState[productId]?.tariffVerificationValues ?? [];

    // adderResume already carries the correct schedule for this product's selling mode: the
    // per-product flow only ever sets selectedDate/selectedTariffHour when the calendar/time
    // steps apply, and the single-booking flow nulls them out per-product in
    // applySingleBookingSelectionAndCompleteBooking before this runs. Re-deriving them here from
    // productSelector's sellingMode is redundant when it works and wipes a valid schedule when the
    // product hasn't been loaded into state.product.products yet — which broke multi_booking too.
    const item = createAddToCartItemRequestPayload({
      productId,
      selectedAdditionals: adderResume.selectedAdditionals,
      selectedResources: adderResume.selectedResources,
      selectedTariffs: adderResume.selectedTariffs,
      selectedDate: adderResume.selectedDate,
      selectedTariffHour: adderResume.selectedTariffHour,
      tariffVerificationValues: tariffVerificationValues,
      selectedAttributes: productSelectionState[productId]?.selectedAttributes ?? [],
    }) as AddCartItemRequest;

    return {
      item,
      productPackageProductId: mapProductIdToProductPackageProductId[productId]!,
    };
  });

  const payload: AddProductPackageToCartRequest = {
    items,
  };

  dispatch(addToCart(payload, widgetId));
};

export const toggleIsClosingAdderModal = (widgetId: string, isClosing: boolean) => (dispatch: AppDispatch) =>
  dispatch(updateIsClosingAdderModal(widgetId, isClosing));

export const closeAdderModal =
  (widgetId: string, packageId?: string) => async (dispatch: AppDispatch, getState: () => RootState) => {
    if (packageId) {
      dispatch(resetAdderStateWithPackage(widgetId, packageId));
    } else {
      const state = getState();
      const productId = productAdderSelectedProduct(widgetId)(state);
      if (productId) {
        dispatch(resetAdderStateWithProduct(widgetId, productId));
      }
    }

    dispatch(clearHourSeatMapValidation(widgetId));
    dispatch(toggleIsClosingAdderModal(widgetId, false));
    dispatch(clearHighlightedProductAdder());
  };

export const packageProductPriceWithDiscountSelector =
  (widgetId: string, packageId: string, productId: string) => (state: RootState) => {
    const { tariffsPriceCents, additionalsPriceCents, resourcesPriceCents, totalCents } = adderResumeSelector(
      widgetId,
      productId,
    )(state);

    if (totalCents === 0) return totalCents;

    const productPackageProducts = productPackageProductsSelector(packageId)(state) ?? [];
    const productPackageProduct = productPackageProducts.find((ppp) => ppp.productId === productId);

    const discountPercentage = productPackageProduct?.discountPercentage ?? 0;

    if (!discountPercentage) return totalCents;

    const percentageValue = discountPercentage * tariffsPriceCents;
    const price = tariffsPriceCents - percentageValue;

    return price + additionalsPriceCents + resourcesPriceCents;
  };

export const productPackageTotalPriceSelector = (widgetId: string, packageId: string) => (state: RootState) => {
  const { productSelectionState } = adderState(widgetId)(state);

  const totalPrice = Object.entries(productSelectionState).reduce((acc, [id]) => {
    const currTotalCents = packageProductPriceWithDiscountSelector(widgetId, packageId, id)(state);

    acc += currTotalCents;

    return acc;
  }, 0);

  return totalPrice;
};

export const productPackageProductDiscountSelector =
  (id: string, productId: string, packageId = '') =>
  (state: RootState) => {
    if (!packageId) {
      return;
    }

    const productPackageProducts = productPackageProductsSelector(packageId)(state) ?? [];
    const productPackageProduct = productPackageProducts.find((ppp) => ppp.productId === productId);

    return productPackageProduct?.discountPercentage ?? 0;
  };

export const productPackageProductsSelector = (packageId: string) => (state: RootState) =>
  packageSelector(packageId)(state)?.productPackageProducts;

export type PackageProductScheduleEntry = {
  sellingMode: SellingMode;
  dates: ProductScheduleState['dates'];
};

const EMPTY_PRODUCT_PACKAGE_PRODUCTS: ProductPackageProduct[] = [];

// Cache selector instances per packageId so createSelector's memoization actually holds across
// renders — every consumer used to get a brand-new array here on every dispatch (even the
// no-op '' packageId of the plain product adder), which made usePackageTimeOptions re-render
// ProductAdderTariff on unrelated state changes.
const packageProductSchedulesSelectorCache = new Map<string, (state: RootState) => PackageProductScheduleEntry[]>();
const MAX_PACKAGE_PRODUCT_SCHEDULES_SELECTOR_CACHE_SIZE = 20;

export const packageProductSchedulesSelector = (
  packageId: string,
): ((state: RootState) => PackageProductScheduleEntry[]) => {
  const cachedSelector = packageProductSchedulesSelectorCache.get(packageId);
  if (cachedSelector) {
    packageProductSchedulesSelectorCache.delete(packageId);
    packageProductSchedulesSelectorCache.set(packageId, cachedSelector);
    return cachedSelector;
  }

  const selector = createSelector(
    (state: RootState) => productPackageProductsSelector(packageId)(state) ?? EMPTY_PRODUCT_PACKAGE_PRODUCTS,
    (state: RootState) => state.product.productSchedules,
    (productPackageProducts, productSchedules): PackageProductScheduleEntry[] =>
      productPackageProducts
        .filter((item) => item.product)
        .map((item) => ({
          sellingMode: item.product!.sellingMode,
          dates: productSchedules[item.productId]?.dates ?? {},
        })),
  );

  packageProductSchedulesSelectorCache.set(packageId, selector);
  if (packageProductSchedulesSelectorCache.size > MAX_PACKAGE_PRODUCT_SCHEDULES_SELECTOR_CACHE_SIZE) {
    const oldestKey = packageProductSchedulesSelectorCache.keys().next().value;
    if (oldestKey !== undefined) packageProductSchedulesSelectorCache.delete(oldestKey);
  }

  return selector;
};

export const productPackageStatusSelector = (packageId: string) => (state: RootState) =>
  packageSelector(packageId)(state)?.status;

export const productPackageErrorSelector = (packageId: string) => (state: RootState) =>
  packageSelector(packageId)(state)?.error;

export const productPackageCurrentProductIndexSelector = (packageId: string) => (state: RootState) =>
  packageSelector(packageId)(state)?.currentProductIndexAtAdder;

export const productPackageSelector = (packageId: string) => (state: RootState) =>
  packageSelector(packageId)(state)?.productPackage;

export const checkIfThePackageHasInactiveProductSelector = (packageId: string) => (state: RootState) => {
  const productPackageProducts = productPackageProductsSelector(packageId)(state) ?? [];

  return productPackageProducts.some((productPackageProducts) => !productPackageProducts.product!.active);
};

export const isClosingAdderModalSelector = (widgetId: string) => (state: RootState) =>
  adderState(widgetId)(state).isClosingAdderModal;

export const packageIdSelector = (widgetId: string) => (state: RootState) =>
  adderState(widgetId)(state).packageId || '';

export const isProductPackage = (widgetId: string) => (state: RootState) => packageIdSelector(widgetId)(state) !== '';

export const adderStateAndCurrentProductIdSelector = (widgetId: string) => (state: RootState) => {
  const adder = adderState(widgetId)(state);
  const productId = adder.productId;

  return { adderState: adder, productId: productId || '' };
};

export const productSelectionStateSelector = (widgetId: string) => (state: RootState) =>
  adderState(widgetId)(state).productSelectionState;

export const tariffVerificationsSelector = (tariffTypeId: string) => (state: RootState) =>
  state.product.tariffVerifications[tariffTypeId] || [];

export const loadingTariffVerificationsSelector = (tariffTypeId: string) => (state: RootState) =>
  state.product.loadingTariffVerifications[tariffTypeId] || false;

export const loadingAnyTariffVerificationsSelector = (state: RootState) =>
  Object.values(state.product.loadingTariffVerifications).some(Boolean);

const EMPTY_TARIFF_VERIFICATION_VALUES: TariffWithVerificationValues[] = [];

export const allTariffVerificationsSelector = (state: RootState) => state.product.tariffVerifications;

export const tariffVerificationValuesSelector = (widgetId: string, productId?: string) => (state: RootState) => {
  const { adderState, productId: currentProductId } = adderStateAndCurrentProductIdSelector(widgetId)(state);
  const resolvedProductId = productId ?? currentProductId;

  if (!resolvedProductId) {
    return EMPTY_TARIFF_VERIFICATION_VALUES;
  }

  return (
    adderState.productSelectionState[resolvedProductId]?.tariffVerificationValues ?? EMPTY_TARIFF_VERIFICATION_VALUES
  );
};

export const hourSeatMapValidationStatusSelector =
  (widgetId: string) =>
  (state: RootState): HourSeatMapValidationStatus =>
    state.product.hourSeatMapValidation[widgetId]?.status ?? 'idle';

export const hourSeatMapValidationResultsSelector =
  (widgetId: string) =>
  (state: RootState): { [key: string]: SeatMapAllocationAttempt } =>
    state.product.hourSeatMapValidation[widgetId]?.results ?? {};

export const hourSeatMapValidationTariffHashSelector =
  (widgetId: string) =>
  (state: RootState): string =>
    state.product.hourSeatMapValidation[widgetId]?.tariffHash ?? '';
